<template>
  <v-app>
    <mainMenu></mainMenu>
    <v-container fluid class="py-0">
      <!-- Search zip code to find cars -->
      <v-row class="sticky-search mb-0 mt-n4">
        <v-col cols="12" sm="6" md="4" class="pt-0 pb-1">
          <v-text-field
            v-model="zipCode"
            label="รหัสไปรษณีย์"
            ref = "inputZipcode"
            :rules="[rules.required, rules.zipCode]"
            @input="handleZipCodeChange"
            maxlength="5"
            clearable
            @click:clear="clearLocation"
            type="number"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            dense 
            hide-details="auto"
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="formattedLocation"
            label="ที่อยู่"
            readonly
            dense
            hide-details
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="5" md="4" lg="3" class="pt-0 pb-0">
          <!-- Telephone number search -->
          <v-text-field
            outlined
            v-model="telephoneSearch"
            label="ใส่เบอร์โทรศัพท์เพื่อค้นหานัด"
            @keydown.enter="searchByTelephone"
            clearable
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          >
            <template v-slot:append-outer>
              <v-btn fab small color="orange accent-4" style="margin-top: -8px;" @click="searchByTelephone">
                <v-icon color="white">mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-text-field>
          <div v-if="!canBookHere" class="d-flex align-start mt-n3 mb-0">
            <v-icon x-small color="orange darken-2" class="mt-1 mr-1">mdi-information</v-icon>
            <span class="caption orange--text text--darken-3" style="line-height: 1.3;">
              หน้านี้ใช้ดูและค้นหานัดหมายเท่านั้น — จองวันส่งได้ที่หน้า
              <router-link :to="{ name: 'bookingDate' }">จองวันส่งสินค้า</router-link>
            </span>
          </div>
          <div v-else class="d-flex align-start mt-n3 mb-0">
            <v-icon x-small color="grey darken-1" class="mt-1 mr-1">mdi-information</v-icon>
            <span class="caption grey--text text--darken-1" style="line-height: 1.3;">
              สามารถนัดหมายได้ไม่เกิน {{ MAX_APPOINTMENTS_SLOT }} นัดหมายต่อช่วงเวลา และวันนึงไม่เกิน {{ MAX_APPOINTMENTS_DAY }} นัดหมาย
            </span>
          </div>
        </v-col>
      </v-row>
      <!-- <div>{{ user }}</div> -->
      <!-- <v-btn color="red darken-1" @click="test2()">เพิ่ม zonedv</v-btn> -->
      <!-- showBy is the tab's position: 0 = แสดงตามวัน, 1 = แสดงตามรถ.
           Reordering these tabs means changing both v-if blocks below. -->
      <v-tabs v-model="showBy" class="mt-0">
        <v-tab :key="0"><v-icon>mdi-calendar-range</v-icon>แสดงตามวัน</v-tab>
        <v-tab :key="1"><v-icon>mdi-car-clock</v-icon>แสดงตามรถ</v-tab>
      </v-tabs>
      
      <!-- display booking list by cars -->
      <div v-if="showBy === 1">
        <div v-for="(car, carIndex) in cars" :key="car.id"
                class="car-row car-container"
                :class="{'even-row': carIndex % 2 === 0}">
          <!-- <v-col cols="6" sm="6" md="4" class="d-flex align-center"> -->
          <div class="sticky-car-info">
            <v-row>
              <v-col cols="4" sm="2" md="2" lg="1">
                <div class="car-name pl-5">{{ car.name }}</div>
              </v-col>
              <v-col cols="8" sm="8" md="6" lg="6">
                {{ car.driverName }}
              </v-col>
            </v-row>
          </div>
          
          <div class="car-content">
            <v-col cols="12">
              <div class="flex-wrap">
                <div v-for="(day, dayIndex) in days" :key="dayIndex"
                      class="date-slot"
                      :class="{'sunday': isClosedSunday(day.date)}">
                  <div class="text-caption text-center mb-1 slot-heading" @click="showSlotDetails(carIndex, dayIndex, null)" :style="getDayColor(carIndex, dayIndex)">
                    {{ formatThDate(day.date) }}
                  </div>
                  <div v-if="routeHolds[localYmd(day.date) + '|' + car.name]" class="text-caption text-center indigo--text route-hold"
                       :title="routeHoldTitle(day.date, car.name)" @click="openRouteDeparture(carIndex, day.date)">
                    ติดเส้นทางนอกพื้นที่
                  </div>
                  
                  <!-- Morning slot -->
                  <v-chip
                    :color="getSlotColor(carIndex, dayIndex, 'AM')"
                    dark
                    @click="showSlotDetails(carIndex, dayIndex, 'AM')"
                    class="mb-1 time-slot"
                    :class="{'sunday-slot': isClosedSunday(day.date)}"
                  >
                    <v-icon small class="mr-2">mdi-white-balance-sunny</v-icon>
                    <span class="ml-auto">{{ getSlotStatus(carIndex, dayIndex, 'AM') }}</span>
                    <span class="separator">{{ getSlotBookingSymbol(carIndex, dayIndex, 'AM') }}</span>

                    <!-- <v-icon small class="ml-2">mdi-lock</v-icon> -->
                  </v-chip>
                  
                  <!-- Afternoon slot -->
                  <v-chip
                    :color="getSlotColor(carIndex, dayIndex, 'PM')"
                    dark
                    @click="showSlotDetails(carIndex, dayIndex, 'PM')"
                    class="mb-1 time-slot"
                    :class="{'sunday-slot': isClosedSunday(day.date)}"
                  >
                    <v-icon small class="mr-2">mdi-weather-night</v-icon>
                    <span class="ml-auto">{{ getSlotStatus(carIndex, dayIndex, 'PM') }}</span>
                    <!-- <span v-if="getSlotCount(carIndex, dayIndex, 'PM') === 1" class="separator">.</span> -->
                    <!-- <span v-else-if="getSlotCount(carIndex, dayIndex, 'PM') === 2" class="separator">:</span> -->
                    <!-- <span v-else class="separator invisible">:</span> -->
                    <span class="separator">{{ getSlotBookingSymbol(carIndex, dayIndex, 'PM') }}</span>
                    <!-- <span>{{ isSlotAvailable(carIndex, dayIndex, 'PM') }}</span> -->
                  </v-chip>

                  <!-- Other slot -->
                  <v-chip
                    v-if="cars[carIndex].slots[dayIndex].OM.length>0"
                    :color="getSlotColor(carIndex, dayIndex, 'OM')"
                    dark
                    @click="showSlotDetails(carIndex, dayIndex, 'OM')"
                    class="time-slot"
                    :class="{'sunday-slot': isClosedSunday(day.date)}"
                  >
                    <v-icon small class="mr-2">mdi-help</v-icon>
                    <!-- <span class="ml-auto">{{'' + cars[carIndex].slots[dayIndex].OM.length +''}}</span> -->
                    <span class="ml-auto">{{ getSlotStatus(carIndex, dayIndex, 'OM') }}</span>
                    <span class="separator invisible">:</span>
                  </v-chip>
                  <div style="font-size: 13px;text-align: center;">{{ car.slots[dayIndex].district }}</div>
                </div>
              </div>
            </v-col>
          </div>
        </div>
      </div>
      
      <!-- display booking list by date -->
      <div v-if="showBy === 0">
        <!-- Car names header (display only on desktop) -->
        <v-row class="d-none d-md-block sticky-car-header">
          <v-col :cols="11" offset="1" class="d-flex flex-wrap">
            <div
              v-for="(car, carIndex) in cars"
              :key="carIndex"
              class="text-center"
              style="min-width: 80px;font-size: 13px;font-weight: 500;"
            >
              <!-- <v-col cols="12">{{ car.name }}</v-col> -->
               {{ car.name }}
            </div>
          </v-col>
        </v-row>

        <!-- Days and cars content -->
        <div v-for="(day, dayIndex) in days" :key="dayIndex" class="day-row">
          <!-- Display the date as a row (mobile only) -->
          <v-row class="d-flex d-sm-none">
            <v-col cols="12" class="text-left py-2 date-header">
              {{ formatThDate(day.date) }}
            </v-col>
          </v-row>

          <!-- Display slots for each car -->
          <v-row>
            <!-- Display the date (desktop only) -->
            <v-col
              :md="1"
              class="align-center d-none d-sm-flex"
            >
              <div class="day-name text-caption text-center">
                {{ formatThDate(day.date) }}
              </div>
            </v-col>

            <!-- Display slots for each car -->
            <v-col :cols="11" :md="11" class="d-flex flex-wrap">
              <div
                v-for="(car, carIndex) in cars"
                :key="car.id"
                class="car-slot text-center"
                :class="{
                  'even-row': dayIndex % 2 === 0,
                  'odd-row': dayIndex % 2 !== 0,
                  'sunday': isClosedSunday(day.date)
                }"
              >
                <!-- Display car index + 1 -->
                <div class=" justify-center slot-heading" @click="showSlotDetails(carIndex, dayIndex, null)" :style="getDayColor(carIndex, dayIndex)">
                  {{ carIndex + 1 }}
                </div>
                <!-- Day 2 of a route sent as 2 days: the car is still out. Its
                     jobs stay on the departure day (one route, one sheet); this
                     opens them there. -->
                <div v-if="routeHolds[localYmd(day.date) + '|' + car.name]" class="text-caption indigo--text route-hold"
                     :title="routeHoldTitle(day.date, car.name)" @click="openRouteDeparture(carIndex, day.date)">
                  ติดเส้นทาง
                </div>
                
                <!-- Morning slot -->
                <v-chip
                  :color="getSlotColor(carIndex, dayIndex, 'AM')"
                  dark
                  @click="showSlotDetails(carIndex, dayIndex, 'AM')"
                  class="mb-1 time-slot"
                  :class="{'sunday-slot': isClosedSunday(day.date)}"
                >
                  <v-icon small class="mr-2">mdi-white-balance-sunny</v-icon>
                  <span class="ml-auto">{{ getSlotStatus(carIndex, dayIndex, 'AM') }}</span>
                  <!-- <span v-if="getSlotCount(carIndex, dayIndex, 'AM') === 1" class="separator">.</span> -->
                  <!-- <span v-else-if="getSlotCount(carIndex, dayIndex, 'AM') === 2" class="separator">:</span> -->
                  <!-- <span v-else class="separator invisible">:</span> -->
                  <span class="separator">{{ getSlotBookingSymbol(carIndex, dayIndex, 'AM') }}</span>
                </v-chip>

                <!-- Afternoon slot -->
                <v-chip
                  :color="getSlotColor(carIndex, dayIndex, 'PM')"
                  dark
                  @click="showSlotDetails(carIndex, dayIndex, 'PM')"
                  class="time-slot"
                  :class="{'sunday-slot': isClosedSunday(day.date)}"
                >
                  <v-icon small class="mr-2">mdi-weather-night</v-icon>
                  <span class="ml-auto">{{ getSlotStatus(carIndex, dayIndex, 'PM') }}</span>
                  <!-- <span v-if="getSlotCount(carIndex, dayIndex, 'PM') === 1" class="separator">.</span> -->
                  <!-- <span v-else-if="getSlotCount(carIndex, dayIndex, 'PM') === 2" class="separator">:</span> -->
                  <!-- <span v-else class="separator invisible">:</span> -->
                  <span class="separator">{{ getSlotBookingSymbol(carIndex, dayIndex, 'PM') }}</span>
                </v-chip>

                <!-- Other slot -->
                <v-chip
                  v-if="cars[carIndex].slots[dayIndex].OM.length>0"
                  :color="getSlotColor(carIndex, dayIndex, 'OM')"
                  dark
                  @click="showSlotDetails(carIndex, dayIndex, 'OM')"
                  class="time-slot"
                  :class="{'sunday-slot': isClosedSunday(day.date)}"
                >
                  <v-icon small class="mr-2">mdi-help</v-icon>
                  <span class="ml-auto">{{'' + cars[carIndex].slots[dayIndex].OM.length +''}}</span>
                  <span class="separator invisible">:</span>
                </v-chip>
                <div style="font-size: 13px;text-align: center;">{{ car.slots[dayIndex].district }}</div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-container>

    <!-- Booking car dialog -->
    <v-dialog v-model="bookingCarDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">จองรถวันที่ {{ selectedDate }} ช่วง {{ selectedSlotTime }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  label="ชื่อ - นามสกุล"
                  v-model="CarBookingForm.name"
                  :error-messages="nameError"
                  required
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="โทรศัพท์"
                  v-model="CarBookingForm.telephone"
                  :error-messages="telephoneError"
                  required
                  clearable
                  hint="เบอร์ต่างประเทศ: + รหัสประเทศ เช่น +856 20 5555 1234"
                  onkeypress="return (event.charCode >= 48 && event.charCode <= 57) || '+- '.indexOf(event.key) !== -1"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-row>
                  <v-col cols="12">
                    <span>ประเภทสินค้าที่ซื้อ</span>
                    <span v-if="productTypeError" class="error--text ml-2">{{ productTypeError }}</span>
                  </v-col>
                  <v-col cols="12">
                    <div v-for="product in productTypeOptions" :key="product.type" class="product-row">
                      <div class="product-checkbox">
                        <v-checkbox
                          :label="product.type"
                          :value="product.type"
                          v-model="selectedProductTypes"
                          @change="handleProductTypeChange(product)"
                          hide-details
                          class="mt-0"
                          dense
                        ></v-checkbox>
                      </div>
                      <div class="stepper-wrapper">
                        <div v-if="selectedProductTypes.includes(product.type)" class="stepper-input">
                          <button
                            class="stepper-button minus"
                            @click="decrementQuantity(product.type)"
                          >
                            <v-icon>mdi-minus</v-icon>
                          </button>
                          <div class="quantity-display">
                            {{ getProductQuantity(product.type) }}
                          </div>
                          <button
                            class="stepper-button plus"
                            @click="incrementQuantity(product.type)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="ที่อยู่"
                  v-model="CarBookingForm.address"
                  :error-messages="addressError"
                  required
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="ตำบล/แขวง/จังหวัด"
                  :value="formattedLocation"
                  :error-messages="locationError"
                  readonly
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="รหัสไปรษณีย์"
                  :value="zipCode"
                  :error-messages="zipcodeError"
                  readonly
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="bookingCarDialog = false">Close</v-btn>
          <v-btn color="blue darken-1" text @click="submitCarBookingForm">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Slot Details Dialog -->
    <v-dialog v-model="slotDetailsDialog" max-width="98%">
      <v-card>
        <v-card-title class="headline">
          <span>รายละเอียดนัดหมาย</span>
          <span v-if="selectedSlot && selectedSlot.transNo">{{ ` - ใบจัดรถเลขที่ : ${selectedSlot.transNo}` }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="toggleLock" :color="isSlotLocked ? 'red' : 'green'" v-if="selectedSlotTime != 'OM'">
            <v-icon>{{ isSlotLocked ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline' }}</v-icon>
          </v-btn>
          <v-btn icon @click="slotDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          <div class="d-flex align-center">
            <v-icon left>mdi-car</v-icon>
            <span>{{ selectedCarName }}</span>
            <span v-if="selectedSlot && selectedSlot.driverName">
              <!-- <v-icon right>mdi-card-account-details-outline</v-icon> -->
              <v-icon right>mdi-account</v-icon>
              {{ ` ${selectedSlot.driverName}` }}
            </span>
            <span v-if="selectedSlot && selectedSlot.totalVolume">
              <!-- <v-icon right>mdi-land-plots</v-icon> -->
              <v-icon right>mdi-cube-outline</v-icon>
              {{ ` ${selectedSlot.totalVolume}` }}
            </span>
            <v-spacer></v-spacer>
            <span style="padding-right: 10px;">
              <span>วันนัดส่งของ&nbsp;</span>
              <v-icon>mdi-calendar</v-icon>
              <span>{{ selectedSlotDate }}</span>
            </span>
            <span v-if="selectedSlot">
              <span>วันขึ้นของ&nbsp;</span>
              <v-icon>mdi-calendar</v-icon>
              <span>{{ formatStrThDate(selectedSlot.loadDate) }}</span>
            </span>
            <v-spacer></v-spacer>
            <v-icon class="mr-2" v-if="!selectedSlotTime&&selectedSlot&&selectedSlot.transNo" style="cursor: pointer;" @click="printDelivery(selectedSlot.transNo)">mdi-printer</v-icon>
            <v-icon left>{{ selectedSlotTime === 'AM' ? 'mdi-white-balance-sunny' : selectedSlotTime === 'PM' ? 'mdi-weather-night' : 'mdi-help' }}</v-icon>
            <span>{{ selectedSlotTime === 'AM' ? 'ช่วงเช้า' : selectedSlotTime === 'PM' ? 'ช่วงบ่าย' : 'ช่วงอื่นๆ' }}</span>
          </div>
        </v-card-subtitle>
        
        <v-divider></v-divider>
        <v-card-text>
          <!-- fixed-header + the max-height in .slot-details-table keep the
               table's own scrollbars on screen: without them a long day made
               the card taller than the window, and the horizontal scrollbar
               sat below the fold while the last column was cut off. -->
          <v-simple-table fixed-header class="slot-details-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">ลำดับ</th>
                  <th class="text-center">เวลา</th>
                  <th class="text-left" style="text-align: center !important;">เลขที่ SO</th>
                  <th class="text-left" style="min-width: 120px;">ชื่อลูกค้า</th>
                  <th class="text-left">เบอร์โทร</th>
                  <th class="text-left" style="min-width: 220px;">ที่อยู่</th>
                  <th class="text-center">พิกัด</th>
                  <th class="text-left">ประเภทสินค้า</th>
                  <th class="text-left" style="min-width: 150px;">รหัสสินค้า</th>
                  <th class="text-left" style="min-width: 120px;">สีหน้าบาน|โครงตู้</th>
                  <th class="text-left">จำนวน</th>
                  <th class="text-left">Zone</th>
                  <!-- จัดรถ and ลบ share one column, and ผู้จอง moved under the
                       customer's name: as three columns they pushed a day with
                       unconfirmed bookings past the dialog's width. -->
                  <th v-if="slotHasBookings || slotShowsDelete" class="text-center">
                    {{ slotHasBookings && slotShowsDelete ? 'จัดรถ / ลบ' : slotHasBookings ? 'จัดรถ' : 'ลบ' }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- <tr v-for="(booking, index) in selectedSlotBookingsSorted" :key="index"> -->
                <template v-for="(booking, index) in displayBookings">
                <tr v-if="booking.dayHead" :key="'head-' + index" class="plan-day-head">
                  <td colspan="13">
                    <v-icon small color="indigo">mdi-calendar-arrow-right</v-icon>
                    {{ booking.dayHead.day ? 'วันที่ ' + booking.dayHead.day + ' — ' : '' }}{{ formatStrThDate(booking.dayHead.date) }}
                  </td>
                </tr>
                <tr :key="index">
                  <td><div>{{ index + 1 }}</div></td>
                  <td >
                    <div class="time-cell" :class="{'editable': RJ.isListRow(booking) && (isAdmin() || userRole === 'delivery'),'non-editable': !RJ.isListRow(booking)}"
                      @click="RJ.isListRow(booking) && (isAdmin() || userRole === 'delivery')?editTransportForm(booking,index):null">
                      {{ booking.time || '-' }}
                    </div>
                  </td>
                  <td>
                    <div class="so-number-cell" :class="{'editable': canBookHere && !RJ.isListRow(booking) && !RJ.isReturnOnly(booking),'non-editable': !canBookHere || RJ.isListRow(booking) || RJ.isReturnOnly(booking)}"
                      @click="canBookHere && !RJ.isListRow(booking) && !RJ.isReturnOnly(booking) ? inputSoOpen(booking) : null">
                      {{ RJ.isAppointment(booking) ? '—' : RJ.refText(booking) }}
                    </div>
                  </td>
                  <td>
                    <div>{{ booking.name || booking.custName || '-' }}</div>
                    <div v-if="slotHasBookings && booking.user" class="caption grey--text text--darken-1">
                      <v-icon x-small>mdi-account-edit-outline</v-icon> {{ $personName(booking.user) }}
                    </div>
                  </td>
                  <td>{{ booking.telephone || booking.custTel || '-' }}</td>
                  <td>
                    <div v-if="booking.address">{{ booking.address +' '+formatThaiLocation(booking.location) }}</div>
                    <div v-else-if="booking.custAddress">{{ booking.custAddress }}</div>
                    <div v-else>-</div>
                    <div v-if="booking.landmark" class="caption grey--text text--darken-2">
                      <v-icon x-small>mdi-flag-outline</v-icon> จุดสังเกต: {{ booking.landmark }}
                    </div>
                  </td>
                  <!-- พิกัด. Every role that reaches this page may set it, shops
                       included: the pin is the one thing a driver's นำทาง goes to
                       directly, and a shop that knows its customer saves delivery
                       the phone call. -->
                  <td class="text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on" @click="openPinDialog(booking)">
                          <v-icon :color="hasPin(booking) ? 'green darken-1' : 'grey lighten-1'">
                            {{ hasPin(booking) ? 'mdi-map-marker-check' : 'mdi-map-marker-off-outline' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ hasPin(booking) ? 'มีพิกัดแล้ว — คลิกเพื่อแก้ไข' : 'ยังไม่มีพิกัด — คลิกเพื่อเพิ่ม' }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    <div v-if="booking.productTypes">
                      <div v-for="(product, pIndex) in booking.productTypes" :key="pIndex">
                        {{ product.type }}
                      </div>
                    </div>
                    <div v-else>-</div>
                  </td>
                  <td>
                    <div v-if="booking.prodList">
                      <div v-for="(product, pIndex) in booking.prodList" :key="pIndex" style="min-height: 24px;">
                        {{ product.prodCode }}
                      </div>
                    </div>
                    <div v-else>-</div>
                  </td>
                  <td>
                    <div v-if="booking.prodList">
                      <div v-for="(product, pIndex) in booking.prodList" :key="pIndex" style="min-height: 24px;">
                        {{ product.front }}{{ product.frame ? ' | ' + product.frame : '' }}
                      </div>
                    </div>
                    <div v-else>-</div>
                  </td>
                  <td>
                    <div v-if="booking.productTypes">
                      <div v-for="(product, pIndex) in booking.productTypes" :key="pIndex" style="min-height: 24px;">
                        {{ product.quan }}
                      </div>
                    </div>
                    <div v-else-if="booking.prodList">
                      <div v-for="(product, pIndex) in booking.prodList" :key="pIndex" style="min-height: 24px;">
                        {{ product.qty }}
                      </div>
                    </div>
                    <div v-else>-</div>
                  </td>
                  <td>
                    <div v-if="booking.zoneMain">{{ booking.zoneMain }}</div>
                    <div v-else>-</div>
                  </td>
                  <td v-if="slotHasBookings || slotShowsDelete" class="text-center" style="white-space: nowrap;">
                    <v-btn v-if="booking.slot" color="success"
                      small :disabled="(!isAdmin()  && userRole !== 'delivery') || (!booking.soNo && !booking.rtNo)"
                      @click="approveBooking(booking,index)">
                      <v-icon small>mdi-check</v-icon>ยืนยัน
                    </v-btn>
                    <v-btn v-if="RJ.isListRow(booking) && (isAdmin()||userRole==='delivery')" icon color="error" @click="deleteBooking(booking)">
                      <v-icon>mdi-delete</v-icon>{{ booking.status }}
                    </v-btn>
                    <v-btn v-if="!booking.status && canBookHere && ownsBooking(booking)" icon color="error" @click="deleteBooking(booking)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
          
          <!-- <div v-if="selectedSlotBookings.length === 0" class="text-center py-4"> -->
          <div v-if="displayBookings.length === 0" class="text-center py-4">
            <v-icon large color="grey">mdi-information-outline</v-icon>
            <div class="subtitle-1 grey--text">ไม่มีนัดหมายในช่วงเวลานี้</div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="isAdmin() || userRole === 'delivery'" color="purple lighten-2" style="color: #fff" @click="inputSoOpen(null)">
            <v-icon left>mdi-plus</v-icon>
            เพิ่ม SO
          </v-btn>
          <v-btn 
            v-if="canBookHere"
            color="primary" 
            @click="createNewCarBooking(selectedCarIndex, selectedDayIndex, selectedSlotTime)"
            :disabled="!isSlotAvailable(selectedCarIndex, selectedDayIndex, selectedSlotTime)||isSlotLocked"
          >
            <v-icon left>mdi-plus</v-icon>
            เพิ่มนัดหมาย
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- พิกัด / จุดสังเกต Dialog: shared with จัดรถ (managecar.vue). -->
    <list-pin-dialog v-model="pinDialog.open" :row="pinDialog.row" :address="pinAddress(pinDialog.row)" />

    <!-- SO Input Dialog -->
    <v-dialog v-model="soDialog" max-width="500px" ref="soDialog">
      <v-card>
        <v-card-title class="headline">
          <span>ระบุเลขที่ SO</span>
        </v-card-title>
        
        <v-card-text>
          <v-text-field
            v-model="soNumber"
            label="เลขที่ SO"
            ref="inputSo"
            outlined
            clearable
            :rules="[
              v => !!v || 'กรุณาระบุเลขที่ SO',
              v => /^\d+$/.test(v) || 'เลขที่ SO ต้องเป็นตัวเลขเท่านั้น',
              v => v.length >= 9 || 'เลขที่ SO ต้องมากกว่า 9 หลัก'
            ]"
          ></v-text-field>
        </v-card-text>
        
        <v-card-actions>
          <!-- Add empty space to push delete button right -->
          <div style="width: 16px;"></div>
          
          <!-- Delete button aligned with title -->
          <v-btn 
            v-if="selectedBooking && selectedBooking.soNo"
            color="error" 
            text 
            @click="inputSoDelete"
            class="ml-0"
          >
            ลบ SO
          </v-btn>
          
          <v-spacer></v-spacer>
          
          <v-btn color="grey darken-1" text @click="soDialog = false">
            ยกเลิก
          </v-btn>
          <v-btn 
            color="primary" 
            @click="selectedBooking===null?inputSoAddSoNew():inputSoAddSoFromBooking()"
            :disabled="!soNumber || !/^\d{9,}$/.test(soNumber)"
          >
            ตกลง
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Transport Details & TimeService Dialog -->
    <v-dialog v-model="showTransportDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          <span>ข้อมูลการจัดส่ง</span>
          <span> - {{transportFormMode}}</span>
          <!-- <span> - {{selectedSlotTime}}</span> -->
        </v-card-title>
        <v-card-text>
          <v-form ref="transportForm" lazy-validation>
            <v-row>
              <v-col cols="6">
                <!-- เลขที่ใบจัดส่ง -->
                <v-text-field
                  v-model="selectedSlotNewTransNo"
                  label="เลขที่ใบจัดส่ง"
                  readonly
                  prepend-icon="mdi-clipboard-minus-outline"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <!-- เลขที่ SO -->
                <!-- :value="selectedBooking?selectedBooking.soNo:soNumber" -->
                <v-text-field
                  :value="transportRefText"
                  v-if="selectedBooking||soNumber"
                  :label="rtNumber ? 'เลขที่ SO / RT' : 'เลขที่ SO'"
                  readonly
                  prepend-icon="mdi-clipboard-minus-outline"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <!-- ทะเบียนรถ (Read-only) -->
                <v-text-field
                  v-model="transportForm.carNumber"
                  label="ทะเบียนรถ"
                  readonly
                  prepend-icon="mdi-car"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="8">
                <!-- ชื่อคนขับ -->
                <v-select
                  v-model="transportForm.driverCode"
                  :items="driverLst"
                  item-text="driverName"
                  item-value="driverCode"
                  label="ชื่อคนขับ"
                  :rules="[v => !!v || 'กรุณาเลือกคนขับ']"
                  required
                  clearable
                  no-data-text="ไม่พบข้อมูลคนขับ"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-account</v-icon>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.driverName }} (รหัส: {{ item.driverCode }})
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.driverName }}</v-list-item-title>
                      <v-list-item-subtitle>รหัส: {{ item.driverCode }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- วันที่ขึ้นของ -->
            <v-menu
              v-model="loadDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="transportForm.loadDate"
                  label="วันที่ขึ้นของ"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="[v => !!v || 'กรุณาเลือกวันที่ขึ้นของ']"
                  required
                >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-calendar</v-icon>
                </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="transportForm.loadDate"
                no-title
                scrollable
                locale="th"
                :max="calculateMaxLoadDate()"
              ></v-date-picker>
            </v-menu>

            <!-- วันที่จัดส่ง (Read-only) -->
            <v-text-field
              :value="transportForm.deliveryDate"
              label="วันที่จัดส่ง"
              readonly
              prepend-icon="mdi-calendar"
              hide-details
            >
            </v-text-field>

            <!-- เวลาจัดส่ง (Start & End Time) -->
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="transportForm.startTime"
                  label="เริ่มส่งเวลา"
                  v-mask="'##:##'"
                  suffix="น."
                  :rules="[
                    v => !v || /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(v) || 'รูปแบบเวลาไม่ถูกต้อง (HH:MM)',
                    // () => !transportForm.startTime || !transportForm.endTime || this.validateTimeRange() || 'เวลาสิ้นสุดต้องหลังเวลาเริ่มต้น'
                    () => {
                      if (transportForm.startTime && !transportForm.endTime) {
                        return 'กรุณาระบุเวลาสิ้นสุดด้วย';
                      }
                      if (!transportForm.startTime && transportForm.endTime) {
                        return 'กรุณาระบุเวลาเริ่มต้นด้วย';
                      }
                      if (transportForm.startTime && transportForm.endTime) {
                        return this.validateTimeRange() || 'เวลาสิ้นสุดต้องหลังเวลาเริ่มต้น';
                      }
                      return true;
                    }
                  ]"
                  clearable
                  placeholder="ไม่ระบุ"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-clock-start</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="transportForm.endTime"
                  label="สิ้นสุดส่งเวลา"
                  v-mask="'##:##'"
                  suffix="น."
                  :rules="[
                    v => !v || /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(v) || 'รูปแบบเวลาไม่ถูกต้อง (HH:MM)',
                    // () => !transportForm.startTime || !transportForm.endTime || this.validateTimeRange() || 'เวลาสิ้นสุดต้องหลังเวลาเริ่มต้น'
                    () => {
                      if (transportForm.startTime && !transportForm.endTime) {
                        return 'กรุณาระบุเวลาสิ้นสุดด้วย';
                      }
                      if (!transportForm.startTime && transportForm.endTime) {
                        return 'กรุณาระบุเวลาเริ่มต้นด้วย';
                      }
                      if (transportForm.startTime && transportForm.endTime) {
                        return this.validateTimeRange() || 'เวลาสิ้นสุดต้องหลังเวลาเริ่มต้น';
                      }
                      return true;
                    }
                  ]"
                  clearable
                  placeholder="ไม่ระบุ"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-clock-end</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="showTransportDialog = false">ยกเลิก</v-btn>
          <v-btn color="primary" @click="onclickOkTransportForm">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Search Zip code Dialog -->
    <v-dialog v-model="showZipCodeDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Select Location</span>
        </v-card-title>
        <v-card-text>
          <v-list>
            <div v-for="(group, district) in groupedZipCodeResults" :key="district" class="d-contents">
              <v-subheader>
                {{ group.province }} - {{ getDistrictPrefix(group.province) }}{{ district }}
              </v-subheader>
              <v-list-item
                v-for="item in group.subDistricts"
                :key="item.sId"
                @click="selectLocation(item)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ getSubDistrictPrefix(group.province) }}{{ item.subDt }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </div>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Telephone Search Dialog -->
    <v-dialog v-model="searchDialog" max-width="98%">
      <v-card>
        <v-card-title class="headline">Booking Information</v-card-title>
        <v-card-text>
          <v-alert v-if="searchError" type="error" class="mb-4">
            {{ searchError }}
          </v-alert>
          
          <v-simple-table v-if="searchResults.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>สถานะ</th>
                  <th>ชื่อลูกค้า</th>
                  <th>ทะเบียนรถ</th>
                  <th>วันที่จัดส่ง</th>
                  <th>เวลา</th>
                  <th>ที่อยู่</th>
                  <th>ผู้จอง</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in searchResults" :key="index">
                  <td>{{ item.status }}</td>
                  <td>{{ item.customerName }}</td>
                  <td>{{ item.carRegNum }}</td>
                  <td>{{ item.deliveryDate }}</td>
                  <td>{{ item.time }}</td>
                  <td style="max-width: 200px;">{{ item.address }}</td>
                  <td>{{ item.user }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="searchDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog progress -->
    <v-dialog v-model="showDialogProgress"  width="unset" persistent>
        <div class="text-center">
            <v-card>
                <v-card-text>
                    <v-progress-circular :size="30" color="orange accent-5" indeterminate></v-progress-circular>
                </v-card-text>
            </v-card>
        </div>
    </v-dialog>
  </v-app>
</template>

<script>
import mainMenu from '../components/mainMenu'
import listPinDialog from '@/components/listPinDialog'
import zoneDv from '../zonedv.json'
import {db} from '@/firebase'
import driverService from '@/components/driverService'
import driverDay from '@/components/driverDay'
import {loadFleet} from '@/components/fleet'
import {isServiceable, areaLabel, resolveFromAddress} from '@/components/serviceArea'
import axios from 'axios'
import {reportError, reportWarning} from '@/components/report'
import R from '@/components/bookingRules'
import RJ from '@/components/returnJob'
import S from '@/components/bookingService'
import P from '@/components/people'
import DR from '@/components/deliveryRow'
import DSV from '@/components/dispatchService'
const MAX_APPOINTMENTS_SLOT = 3;
const MAX_APPOINTMENTS_DAY = 5;
const STATUS_COLORS = {
  UNAVAILABLE: 'grey',
  SUNDAY: 'red',
  OM_SLOT: 'pink lighten-3',
  FULLY_BOOKED: 'pink',
  PARTIALLY_BOOKED: 'yellow darken-2',
  AVAILABLE: 'green lighten-1',
  LOCKED: 'purple darken-2'
};

function getTimeSlot(time) {
  if (!time) return 'OM';

  // If already AM or PM, return immediately
  if (time === 'AM' || time === 'PM') return time;

  // eslint-disable-next-line
  const [startTime, endTime] = time.split(' - ');
  const startHour = parseInt(startTime.split(':')[0], 10);

  if (startHour >= 4 && startHour < 12) return 'AM';
  if (startHour >= 12 && startHour <= 23) return 'PM';
  return 'OM';
}

function getSlotIndex(date) {
const bookingDate = new Date(date);
if (isNaN(bookingDate.getTime())) {
    reportError(`Invalid date: ${date}`);
    return 0;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

const diffInDays = Math.floor((bookingDate - today) / (1000 * 60 * 60 * 24));

return Math.max(0, Math.min(diffInDays, 29));
}

export default {
components: {
  mainMenu,
  listPinDialog
},
data() {
  return {
    MAX_APPOINTMENTS_SLOT,
    MAX_APPOINTMENTS_DAY,
    // returnJob rules, for the grid's status checks and labels.
    RJ,
    // Telephone search
    telephoneSearch:'',
    searchDialog: false,
    searchResults: [],
    searchError: '',

    carRegNumSmall: [], //List of "เลขทะเบียนรถ"
    zipCode: '',
    days: this.generateDays(),
    openSundays: {},  // date -> true, from bookingDay.sundayOpen
    routeHolds: {},   // 'date|plate' -> provinces, a car on day 2 of an out-of-area route
    cars:[],  // id,name(eg.1ฒบ-7172]),slots Array
    maxReservations: 2,
    bookingCarDialog: false,
    
    selectedDate: '',
    productTypeOptions: [
      { type: 'ตู้ใหญ่', vol: 0.24, time: 12 },
      { type: 'ตู้เล็ก', vol: 0.12, time: 10 },
      { type: 'ตู้ KD', vol: 0.06, time: 20 },
      { type: 'อุปกรณ์', vol: 0.012, time: 2 }
    ],
    selectedProductTypes: [],
    CarBookingForm: {
      name: '',
      telephone: '',
      productTypes: [],
      address: '',
      location: '',
      zipcode: '',
      zoneMain: null,
      zoneDv: []
    },
    selectedLocation: '',
    selectedZoneMain: null,
    selectedZoneDv: [],
    showZipCodeDialog: false,
    zipCodeResults: [],
    rules: {
      required: v => !!v || 'Field is required',
      zipCode: v => /^\d{5}$/.test(v) || 'Zip code must be 5 digits'
    },
    nameError: '',
    telephoneError: '',
    productTypeError: '',
    addressError: '',
    locationError: '',
    zipcodeError: '',
    showBy: 0, // แสดงตามวัน, the first tab, on every screen size
    soBookingLst: [],
    bookingLst:[],

    // Event listener
    soBookingUnsubscribe:null,
    bookingUnsubscribe:null,


    // Slot Details Dialog
    slotDetailsDialog: false,
    selectedCarIndex: null,
    selectedDayIndex: null,
    selectedSlotTime: '',   //AM PM OM
    selectedSlot:null,        //selected slot (AM[],PM[],OM[],date,deliveryDate,DriverName,loadDate,totalVolumn,TransNo,zoneDv,zoneMain)
    selectedSlotBookings: [], //Array of booking
    selectedSlotBookingsIndex:null,            //selected index in the slected slot Booking
    selectedCarName: '',      //Car's registration number
    selectedSlotDate: '',     //Date of selected slot
    selectedSlotNewTransNo:'',//New transfer number

    //SO input Dialog
    soDialog: false,
    // พิกัด / จุดสังเกต of one row in the slot dialog -- see openPinDialog.
    pinDialog: { open: false, row: null },
    selectedBooking: null,
    soNumber: '',
    // The return the transport form confirms: the booking's rtNo, and whether the
    // job is a return alone (status 'RT' in deliveryList, no SO to deliver).
    rtNumber: '',
    returnOnly: false,

    //Transport Details Dialog
    showTransportDialog: false,
    transportFormMode:'',
    transportForm: {
      carNumber: "",
      driverCode: "",
      driverName: "",
      deliveryDate: "",
      deliveryTime: "AM",
      loadDate: new Date().toISOString().split('T')[0],
      startTime: '08:00',  // Default start time
      endTime: '17:00'     // Default end time
    },
    deliveryDateMenu: false,
    loadDateMenu: false,

    // Driver list
    driverLst: [], // Array to store all active drivers

    //progress dialog
    showDialogProgress: false,
  };
},
computed: {
  // What the transport form shows as the job's document: SO, SO + RT, or RT.
  transportRefText(){
    if (!this.rtNumber) return this.soNumber;
    return this.soNumber ? this.soNumber + ' · RT ' + this.rtNumber : 'RT ' + this.rtNumber;
  },
  webPath(){
    return this.$store.getters.webPath
  },
  user(){
    return this.$store.state.user
  },
  identities(){
    return this.$store.getters.identities
  },
  userRole() {
    return this.$store.state.status;
  },
  // Shops book on จองวันส่งสินค้า (bookingDate.vue) now; here they only look and
  // search. Every write a shop could reach -- เพิ่มนัดหมาย, ใส่ SO on a booking,
  // deleting their own booking -- is hidden by this and refused again in the
  // method, because a hidden button is not a gate. พิกัด stays open to every role
  // on purpose (see the พิกัด column). Client-side only: Firestore rules are open.
  canBookHere() {
    return R.isStaffRole(this.userRole);
  },
  groupedZipCodeResults() {
    const grouped = this.zipCodeResults.reduce((acc, item) => {
      if (!acc[item.district]) {
        acc[item.district] = {
          province: item.province,
          district: item.district,
          subDistricts: []
        };
      }
      acc[item.district].subDistricts.push(item);
      return acc;
    }, {});

    Object.values(grouped).forEach(group => {
      group.subDistricts.sort((a, b) => this.sortThaiStrings(a.subDt, b.subDt));
    });

    return Object.fromEntries(
      Object.entries(grouped).sort(([a], [b]) => this.sortThaiStrings(a, b))
    );
  },
  formattedLocation() {
    if (!this.selectedLocation) return '';
    
    const [subDistrict, district, province] = this.selectedLocation.split(',');
    
    if (province === 'กรุงเทพมหานคร') {
      return `แขวง${subDistrict} เขต${district} ${province}`;
    } else {
      const districtPrefix = district.startsWith('เมือง') ? 'อำเภอเมือง' : `อำเภอ${district}`;
      return `ตำบล${subDistrict} ${districtPrefix} จังหวัด${province}`;
    }
  },
  // transport dialog
  selectedDriverName() {
    if (!this.transportForm.driverCode) return '';
    const driver = this.driverLst.find(d => d.driverCode === this.transportForm.driverCode);
    return driver ? driver.driverName : '';
  },
  selectedDriverCodeAndName() {
    if (!this.transportForm.driverCode) return '';
    const driver = this.driverLst.find(d => d.driverCode === this.transportForm.driverCode);
    return driver ? `${driver.driverCode} - ${driver.driverName}` : '';
  },
  // slot detail dialog
  selectedSlotBookingsSorted() {
    if (!this.selectedSlotBookings) return [];
    
    // Create a copy of the array to avoid mutating the original
    return [...this.selectedSlotBookings].sort((a, b) => {
      const getSoTime = (item) => {
        if (typeof item.time === 'string') {
          const match = item.time.match(/^(\d{1,2}):(\d{2})/); // extract start time like "13:00"
          if (match) {
            return parseInt(match[1]) * 60 + parseInt(match[2]); // convert to total minutes
          }
        }
        return Infinity; // sort items without time to the end
      };

      return getSoTime(a) - getSoTime(b);
    });
  },
  selectedSlotBookingsSortedCombined() {
    if (!this.selectedSlot) return [];
    
    // Sort each array individually
    const sortByTime = (array) => {
      if (!array) return [];
      return [...array].sort((a, b) => {
        const getTimeValue = (item) => {
          if (!item.time) return Infinity;
          const match = item.time.match(/^(\d{1,2}):(\d{2})/);
          return match ? parseInt(match[1]) * 60 + parseInt(match[2]) : Infinity;
        };
        return getTimeValue(a) - getTimeValue(b);
      });
    };

    // Combine the sorted arrays in AM -> PM -> OM order
    return [
      ...sortByTime(this.selectedSlot.AM),
      ...sortByTime(this.selectedSlot.PM),
      ...sortByTime(this.selectedSlot.OM)
    ];
  },
  // A planned 2-day route is listed in plan order with a heading per day
  // (DR.planOrder): sorted by เวลา alone, its day-2 08:00 drop came first.
  displayBookings(){
    const rows = this.selectedSlotTime
      ? this.selectedSlotBookingsSorted
      : this.selectedSlotBookingsSortedCombined;
    const departure = this.days[this.selectedDayIndex] ? this.localYmd(this.days[this.selectedDayIndex].date) : null;
    return DR.planOrder(rows, departure) || rows;
  },
  // Any unconfirmed booking in the slot: shows ผู้จอง and the ยืนยัน button.
  slotHasBookings(){
    return this.displayBookings.some(booking => booking.slot);
  },
  // Any row the viewer may delete: a confirmed SO for staff, or their own booking.
  slotShowsDelete(){
    return ((this.isAdmin() || this.userRole === 'delivery') && this.displayBookings.some(booking => booking.status))
      || (this.canBookHere && this.displayBookings.some(booking => !booking.status && this.ownsBooking(booking)));
  },
  // isSlotLocked(){
  //   // 0. if don't have this.selectedSlot.locked
  //   if(!this.selectedSlot || !this.selectedSlot.locked){
  //     return false
  //   }
  //   const locked = this.selectedSlot.locked;
    
  //   // 1. if this.selectedSlot.locked.am is true and this.selectedSlot.locked.pm is true return true
  //   if (locked.am && locked.pm) {
  //       return true;
  //   }
    
  //   // 2. if this.selectedSlotTime is null and (this.selectedSlot.locked.am and this.selectedSlot.locked.pm)
  //   if (this.selectedSlotTime === null && (locked.am && locked.pm)) {
  //       return true;
  //   }
    
  //   // 3. if this.selectedSlotTime = 'AM' return this.selectedSlot.locked.am
  //   if (this.selectedSlotTime === 'AM') {
  //       return !!locked.am; // Ensure boolean return
  //   }
    
  //   // 4. if this.selectedSlotTime = 'PM' return this.selectedSlot.locked.pm
  //   if (this.selectedSlotTime === 'PM') {
  //       return !!locked.pm; // Ensure boolean return
  //   }
    
  //   // Default case if none of the above conditions are met
  //   return false;
  // }
  isSlotLocked() {
    // 0. Check if selectedSlot exists
    if (!this.selectedSlot) {
      return false;
    }
    
    // 1. If both AM and PM are locked return true
    // if (this.selectedSlot.lockedAm && this.selectedSlot.lockedPm) {
    //   return true;
    // }
    
    // 2. If this.selectedSlotTime is null and both slots are locked
    if (this.selectedSlotTime === null && 
        (this.selectedSlot.lockedAm && this.selectedSlot.lockedPm)) {
      return true;
    }
    
    // 3. If this.selectedSlotTime = 'AM' return lockedAm
    if (this.selectedSlotTime === 'AM') {
      return !!this.selectedSlot.lockedAm; // Ensure boolean return
    }
    
    // 4. If this.selectedSlotTime = 'PM' return lockedPm
    if (this.selectedSlotTime === 'PM') {
      return !!this.selectedSlot.lockedPm; // Ensure boolean return
    }
    
    // Default case if none of the above conditions are met
    return false;
  }

},
watch: {
  soDialog(newVal) {
    if (newVal===true) {
      this.$nextTick(()=>{
        setTimeout(() => {
          this.$refs.inputSo.focus()
        },100)
      })
    }
  }
},
methods: {
  // Made under any email this account has had (people.ownedBy).
  ownsBooking(booking){
    return P.ownedBy(booking.user, this.identities)
  },
  async test2(){
    const filteredData = zoneDv.filter(item => 
      String(item.zip) === "10150" || String(item.zip) === "10160"
    );

    // Add each document with auto-generated ID
    const batch = db.batch(); // Use batch for better performance

    filteredData.forEach(item => {
      const docRef = db.collection('zonedv').doc(); // Auto ID
      batch.set(docRef, {
        ...item, // Spread all properties
      });
    });

    await batch.commit(); // Executes all writes at once

  },
  isAdmin() {
    return this.userRole === 'administrator' || this.userRole === 'admin';
  },
  async searchByTelephone(){
    if (!this.telephoneSearch) {
      this.searchError = 'โปรดใส่เบอร์โทรศัพท์';
      this.searchDialog = true;
      return;
    }
    try {
      this.searchResults = [];
      this.searchError = '';
      
      // Search in booking collection
      const bookingSnapshot = await db.collection('booking')
        .where('telephone', '==', this.telephoneSearch)
        .get();
      
      bookingSnapshot.forEach(doc => {
        const data = doc.data();
        this.searchResults.push({
          status: 'รอจัดรถ',
          customerName: data.name,
          carRegNum: data.carName,
          deliveryDate: data.date,
          time: data.slot,
          address: data.address,
          user: data.user ? this.$personName(data.user) : ''
        });
      });
      
      // Search in deliverList collection
      const deliverListSnapshot = await db.collection('deliveryList')
        .where('custTel', '==', this.telephoneSearch)
        .get();

      // console.log('deliverListSnapshot',deliverListSnapshot)
      
      deliverListSnapshot.forEach(doc => {
        const data = doc.data();
        this.searchResults.push({
          status: 'ยืนยัน',
          customerName: data.custName,
          carRegNum: data.regNum,
          deliveryDate: data.deliveryDate,
          time: data.time,
          address: data.custAddress,
          user:''
        });
      });
      
      if (this.searchResults.length === 0) {
        this.searchError = 'ไม่พบนัดหมาย';
      }

      // Sort by deliveryDate in descending order
      this.searchResults.sort((a, b) => {
        // Convert dates to timestamps for comparison
        const dateA = new Date(a.deliveryDate).getTime();
        const dateB = new Date(b.deliveryDate).getTime();
        
        // For descending order: b - a
        return dateB - dateA;
      });
      
      this.searchDialog = true;
    } catch (error) {
      reportError('Error searching by telephone:', error);
      this.searchError = 'มีเหตุขัดข้อง โปรดติดต่อเจ้าหน้าที่';
      this.searchDialog = true;
    }
  },
  // Show slot details in a dialog
  showSlotDetails(carIndex, dayIndex, slotTime) {
    this.selectedCarIndex = carIndex;
    this.selectedDayIndex = dayIndex;
    this.selectedSlotTime = slotTime;
    
    // Get the slot detail
    this.selectedSlot = this.cars[carIndex].slots[dayIndex]

    // Get the bookings for this slotTime
    this.selectedSlotBookings = this.cars[carIndex].slots[dayIndex][slotTime];
    this.selectedCarName = this.cars[carIndex].name;

    
    // Format the date
    const date = new Date(this.days[dayIndex].date);
    this.selectedSlotDate = date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    
    // Show the dialog
    this.slotDetailsDialog = true;
  },
  
  getDate(dayIndex) {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + dayIndex);

    const formattedDate = targetDate.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
    });

    const year = targetDate.getFullYear() + 543;

    return `${formattedDate} ${year}`;
  },
  
  generateDays() {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date: date,
        dayNumber: i + 1
      });
    }
    return days;
  },
  formatStrThDate(strDate){
    let date = new Date(strDate)
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  },
  formatThDate(date) {
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short'
    });
  },
  getDayColor(carIndex, dayIndex){
    const slot = this.cars[carIndex].slots[dayIndex];
    const hasMissingStatus = slot.AM.some(booking => !booking.status) || 
                              slot.PM.some(booking => !booking.status);
      
      return {
        color: hasMissingStatus ? 'blue' : 'black',
        fontWeight: hasMissingStatus ? 'bold' : 'normal',
        animation: hasMissingStatus ? 'blink 1s infinite' : 'none'
      }
  },
  
  // A Sunday is a day off unless delivery opened it on จองวันส่งสินค้า
  // (bookingDay.sundayOpen); an opened one is shown and confirmed like any day.
  isClosedSunday(date) {
    return date.getDay() === 0 && !this.openSundays[this.localYmd(date)];
  },
  routeHoldTitle(date, carName) {
    const provinces = this.routeHolds[this.localYmd(date) + '|' + carName] || [];
    return 'วันที่ 2 ของเส้นทาง ' + provinces.join(', ') + ' — งานอยู่ในวันออกเดินทาง คลิกเพื่อดู';
  },
  // The departure day of the route holding this car on `date` (the day
  // before -- a route runs at most 2 days), when it is on screen.
  openRouteDeparture(carIndex, date) {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    const ymd = this.localYmd(prev);
    const dayIndex = this.days.findIndex(d => this.localYmd(d.date) === ymd);
    if (dayIndex === -1) return;
    this.showSlotDetails(carIndex, dayIndex, null);
  },
  localYmd(date) {
    const p = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
  },
  
  isSlotAvailable(carIndex, dayIndex, timeSlot) {
    if (
      !this.cars || 
      !this.cars[carIndex] || 
      !this.cars[carIndex].slots || 
      !this.cars[carIndex].slots[dayIndex] || 
      !this.cars[carIndex].slots[dayIndex][timeSlot]
    ) {
      return false;
    }

    // Checked locked status
    // if(this.isSlotLocked) return false;

    // const currentBookings = this.cars[carIndex].slots[dayIndex][timeSlot].length;
    const currentBookings = this.countUniqueBookings(this.cars[carIndex].slots[dayIndex][timeSlot]);
    // const allBookings = this.cars[carIndex].slots[dayIndex].AM.length + 
    //                     this.cars[carIndex].slots[dayIndex].PM.length + 
    //                     this.cars[carIndex].slots[dayIndex].OM.length;
    const allBookings = this.countUniqueBookings(this.cars[carIndex].slots[dayIndex].AM) + 
                        this.countUniqueBookings(this.cars[carIndex].slots[dayIndex].PM) + 
                        this.countUniqueBookings(this.cars[carIndex].slots[dayIndex].OM);

    if (this.isClosedSunday(this.days[dayIndex].date)) {
      return false;
    }

    if (timeSlot === 'OM') {
      return false;
    }

    if (allBookings >= MAX_APPOINTMENTS_DAY) {
      return false;
    }

    // User doesn't input zipCode
    if (this.zipCode === '') {
      return false;
    }

    if (currentBookings >= MAX_APPOINTMENTS_SLOT) {
      return false;
    }

    const slotDate = this.cars[carIndex].slots[dayIndex];

    if(!slotDate.zoneMain){
      return true
    }

    if (!slotDate.zoneDv || !slotDate.zoneDv.includes(this.selectedZoneMain)) {
      return false;
    }

    return true;
  },
  countUniqueBookings(bookings) {
    if (!bookings || !Array.isArray(bookings)) return 0;
    
    const seen = new Set();
    let count = 0;
    
    bookings.forEach(booking => {
      if (!booking) return;
      
      // Count immediately if booking has no status
      if (!booking.hasOwnProperty('status') || booking.status === undefined || booking.status === null || booking.status === '') {
        count++;
        return;
      }
      
      // Only check other conditions for bookings that have status
      // if (booking.custName && booking.custAzip) {
        // const uniqueKey = `${booking.custName.toLowerCase().trim()}_${booking.custAzip.trim()}`;
        const uniqueKey = `${booking.custName.toLowerCase().trim()}_${booking.custAzip.trim()}_${(booking.time||'').trim()}`;
        
        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey);
          count++;
        }
      // }
    });
    
    return count;
  },
  
  getSlotColor(carIndex, dayIndex, timeSlot) {
    // Check locking logic
    if (timeSlot === 'AM' && this.cars[carIndex].slots[dayIndex].lockedAm) {
        return STATUS_COLORS.LOCKED;
    }
    if (timeSlot === 'PM' && this.cars[carIndex].slots[dayIndex].lockedPm) {
        return STATUS_COLORS.LOCKED;
    }


    if (!this.isSlotAvailable(carIndex, dayIndex, timeSlot)) {
      if (this.isClosedSunday(this.days[dayIndex].date)) return STATUS_COLORS.SUNDAY;
      if (timeSlot === 'OM') return STATUS_COLORS.OM_SLOT;
      // if (this.cars[carIndex].slots[dayIndex][timeSlot].length >= MAX_APPOINTMENTS_SLOT) return STATUS_COLORS.FULLY_BOOKED;
      if (this.countUniqueBookings(this.cars[carIndex].slots[dayIndex][timeSlot]) >= MAX_APPOINTMENTS_SLOT) return STATUS_COLORS.FULLY_BOOKED;
      return STATUS_COLORS.UNAVAILABLE;
    }

    // const currentBookings = this.cars[carIndex].slots[dayIndex][timeSlot].length;
    const currentBookings = this.countUniqueBookings(this.cars[carIndex].slots[dayIndex][timeSlot]);
    if (currentBookings > 0 && currentBookings < MAX_APPOINTMENTS_SLOT) {
      return STATUS_COLORS.PARTIALLY_BOOKED;
    }
    return STATUS_COLORS.AVAILABLE;
  },
  
  getSlotStatus(carIndex, dayIndex, timeSlot) {
    if (this.isClosedSunday(this.days[dayIndex].date)) {
      return 'No';
    }
    // const currentBookings = this.cars[carIndex].slots[dayIndex][timeSlot].length;
    const currentBookings = this.countUniqueBookings(this.cars[carIndex].slots[dayIndex][timeSlot]);
    return `${currentBookings}`;
    // return `${currentBookings}/${MAX_APPOINTMENTS_SLOT}`;
  },
  
  getSlotCount(carIndex, dayIndex, timeSlot) {
    const slots = this.cars[carIndex].slots[dayIndex][timeSlot];
    const slotCount = slots.filter(item => item.slot === 'AM' || item.slot === 'PM').length;
    return slotCount;
  },
  getSlotBookingSymbol(carIndex, dayIndex, timeSlot) {
    const slots = this.cars[carIndex].slots[dayIndex][timeSlot]
      .filter(item => item.slot === 'AM' || item.slot === 'PM');
    
    if (slots.length === 0) return '';
    
    // For each slot, map to '*' if has soNo, otherwise '.'
    const separatorChars = slots.map(slot => slot.soNo ? '*' : '.');
    
    // Join the characters with no space between them
    return separatorChars.join('');
  },
  handleZipCodeChange() {
    if (!this.zipCode || this.zipCode.length < 5) {
      this.clearLocation();
    } else {
      this.searchZipCode();
    }
  },
  
  clearLocation() {
    this.zipCode = '';
    this.selectedLocation = '';
    this.zipCodeResults = [];
    this.showZipCodeDialog = false;
  },
  
  async searchZipCode() {
    if (this.zipCode && this.zipCode.length === 5) {
      // zoneMain says how a delivery would be routed; serviceArea says whether
      // the small car serves that address at all. Both have to hold.
      this.zipCodeResults = zoneDv.filter(item =>
        String(item.zip) === String(this.zipCode) &&
        item.zoneMain &&
        isServiceable(item))

      if (this.zipCodeResults.length > 0) {
        this.showZipCodeDialog = true
      } else {
        // Clear before telling the user, not after. isSlotAvailable() treats any
        // non-empty zipCode as usable, so a rejected one left sitting in the
        // field keeps every slot bookable -- and anything that stops the dialog
        // from settling would have skipped a reset placed after the await.
        const rejected = this.zipCode;
        this.clearLocation();
        await this.$root.$message(
          'ไม่พบข้อมูล',
          [
            `รหัสไปรษณีย์ ${rejected} ไม่อยู่ในพื้นที่การจัดส่ง`,
            `พื้นที่ที่จัดส่งได้ : ${areaLabel()}`,
            'ไม่สามารถจองนัดหมายได้'
          ],
          {
            type: 'alert',
            color: 'error',
            width: 400
          }
        );
        // clearLocation() above fixed the state, but not what staff can see:
        // the field keeps its own copy of the value and only syncs when the
        // bound one actually changes -- and within a single input event this
        // one went '' -> '50000' -> '', so as far as the field is concerned
        // nothing changed and the rejected zipcode stays on screen. reset()
        // clears the field's copy.
        if (this.$refs.inputZipcode) this.$refs.inputZipcode.reset()
        this.$nextTick(()=>this.$refs.inputZipcode.focus())
        // Optional: Show a "not found" message to the user
      }
    }
  },
  
  sortThaiStrings(a, b) {
    return a.localeCompare(b, 'th');
  },
  
  selectLocation(item) {
    const location = `${item.subDt},${item.district},${item.province}`
    this.selectedLocation = location
    this.selectedZoneMain = item.zoneMain
    this.selectedZoneDv = item.zoneDv
    this.showZipCodeDialog = false
  },
  
  getDistrictPrefix(province) {
    return province === 'กรุงเทพมหานคร' ? 'เขต' : 'อำเภอ';
  },
  
  getSubDistrictPrefix(province) {
    return province === 'กรุงเทพมหานคร' ? 'แขวง' : 'ตำบล';
  },

  formatThaiLocation(locationStr) {
    const [subdistrict, district, province] = locationStr.split(',');

    // Normalize input
    const provinceTrimmed = province.trim();

    if (provinceTrimmed === 'กรุงเทพมหานคร' || provinceTrimmed.toLowerCase() === 'bangkok') {
      return `แขวง${subdistrict} เขต${district} กรุงเทพมหานคร`;
    } else {
      return `ตำบล${subdistrict} อำเภอ${district} จังหวัด${province}`;
    }
  },
  
  handleProductTypeChange(product) {
    const isSelected = this.selectedProductTypes.includes(product.type);
    if (isSelected) {
      this.CarBookingForm.productTypes.push({
        type: product.type,
        quan: 1,
        vol: product.vol,
        time: product.time
      });
    } else {
      this.CarBookingForm.productTypes = this.CarBookingForm.productTypes.filter(
        item => item.type !== product.type
      );
    }
  },

  getProductQuantity(type) {
    const product = this.CarBookingForm.productTypes.find(item => item.type === type);
    return product ? product.quan : 1;
  },

  incrementQuantity(type) {
    const product = this.CarBookingForm.productTypes.find(item => item.type === type);
    if (product) {
      product.quan = parseInt(product.quan) + 1;
    }
  },

  decrementQuantity(type) {
    const product = this.CarBookingForm.productTypes.find(item => item.type === type);
    if (product && product.quan > 1) {
      product.quan = parseInt(product.quan) - 1;
    }
  },
  
  clearErrors() {
    this.nameError = '';
    this.telephoneError = '';
    this.productTypeError = '';
    this.addressError = '';
    this.locationError = '';
    this.zipcodeError = '';
  },
  validateCarBookingForm() {
    this.clearErrors()
    let isValid = true

    if (!this.CarBookingForm.name) {
      this.nameError = 'กรุณากรอกชื่อ-นามสกุล'
      isValid = false
    }

    if (!this.CarBookingForm.telephone) {
      this.telephoneError = 'กรุณากรอกเบอร์โทรศัพท์'
      isValid = false
    } else if (R.telProblem(this.CarBookingForm.telephone)) {
      this.telephoneError = R.telProblem(this.CarBookingForm.telephone)
      isValid = false
    }

    if (!this.CarBookingForm.address) {
      this.addressError = 'กรุณากรอกที่อยู่'
      isValid = false
    }

    if (!this.CarBookingForm.location) {
      this.locationError = 'กรุณากรอกตำบล/แขวง'
      isValid = false
    }

    if (!this.CarBookingForm.zipcode) {
      this.zipcodeError = 'กรุณากรอกรหัสไปรษณีย์'
      isValid = false
    } else if (!/^\d{5}$/.test(this.CarBookingForm.zipcode)) {
      this.zipcodeError = 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก'
      isValid = false
    }

    return isValid
  },
  createNewCarBooking(carIndex, dayIndex, timeSlot) {
    if (!this.canBookHere) return;
    if (!this.isSlotAvailable(carIndex, dayIndex, timeSlot)) {
      return;
    }

    this.selectedCarIndex = carIndex;
    this.selectedDayIndex = dayIndex;
    this.selectedSlotTime = timeSlot;
    this.selectedDate = this.formatThDate(this.days[dayIndex].date);
    this.clearErrors();
    this.CarBookingForm = {
      name: '',
      telephone: '',
      productTypes: [],
      address: '',
      location: this.selectedLocation,
      zipcode: this.zipCode,
      zoneMain: this.selectedZoneMain,
      zoneDv: this.selectedZoneDv
    };
    this.selectedProductTypes = [];
    this.bookingCarDialog = true;
    this.slotDetailsDialog = false;
  },
  calculateTotals() {
    const totals = this.CarBookingForm.productTypes.reduce((acc, item) => {
      acc.totalVolume += item.vol * item.quan;
      acc.totalTime += item.time * item.quan;
      return acc;
    }, { totalVolume: 0, totalTime: 0 });

    return totals;
  },
  submitCarBookingForm() {
    if (!this.canBookHere) return;
    if (this.validateCarBookingForm()) {
      const { totalVolume, totalTime } = this.calculateTotals();

      const slotDate = this.cars[this.selectedCarIndex].slots[this.selectedDayIndex];
      const slotTime = slotDate[this.selectedSlotTime === 'AM' ? 'AM' : 'PM'] || [];

      if (slotTime.length < MAX_APPOINTMENTS_SLOT) {
        this.showDialogProgress = true;
        const reservationData = {
          ...this.CarBookingForm,
          totalVolume,
          totalTime,
          carName: this.cars[this.selectedCarIndex].name,
          date: slotDate.date,
          slot: this.selectedSlotTime,
          user: this.$store.state.user
        };
        
        if (!slotDate.zoneMain || !slotDate.zoneDv) {
          slotDate.zoneMain = [this.selectedZoneMain];
          slotDate.zoneDv = this.selectedZoneDv;
        } else {
          if (slotDate.zoneDv.includes(this.selectedZoneMain)) {
            if (!slotDate.zoneMain.includes(this.selectedZoneMain)) {
              slotDate.zoneMain.push(this.selectedZoneMain);
            }
            
            this.selectedZoneDv.forEach(zone => {
              if (!slotDate.zoneDv.includes(zone)) {
                slotDate.zoneDv.push(zone);
              }
            });
          } else {
            reportWarning('Selected zone is not allowed for this slot');
            return;
          }
        }

        db.collection("booking").add(reservationData)
          // .then((docRef) => {
          .then(() => {
            this.bookingCarDialog = false;
          })
          .catch((error) => {
            reportError("Error adding document: ", error);
          });
        this.showDialogProgress = false;
      }else {
        this.$root.$message(
          'ช่วงเวลานี้เต็มแล้ว',
          `ไม่สามารถเพิ่มนัดหมายได้ เนื่องจากให้นัดหมายได้ไม่เกิน ${MAX_APPOINTMENTS_SLOT} นัดหมายต่อช่วงเวลาเท่านั้น`,
          { 
            type: 'alert',
            color: 'error'
          }
        );
      }
    }
  },

  
  // The small car serves Bangkok and the provinces next to it, so an SO whose
  // address sits outside that is refused here rather than at dispatch. custAddress
  // is free text from SAP: when it cannot be placed at all we ask staff to confirm
  // instead of blocking a real order over a badly written address.
  // Returns true when the booking may go ahead.
  async checkSoServiceArea(soRecord) {
    const area = resolveFromAddress(soRecord.custAddress, zoneDv);

    if (area.status === 'in') {
      return true;
    }

    if (area.status === 'out') {
      const place = area.province === 'กรุงเทพมหานคร' ? area.province : `จ.${area.province}`;
      await this.$root.$message(
        'อยู่นอกพื้นที่จัดส่ง',
        [
          `ที่อยู่ของ SO เลขที่ ${this.soNumber} อยู่นอกพื้นที่จัดส่ง (${place})`,
          `พื้นที่ที่จัดส่งได้ : ${areaLabel()}`,
          'ไม่สามารถนัดหมายได้'
        ],
        {
          type: 'alert',
          color: 'error',
          width: 450
        }
      );
      return false;
    }

    return await this.$root.$message(
      'ตรวจสอบพื้นที่จัดส่งไม่ได้',
      [
        `ไม่สามารถตรวจสอบจังหวัดจากที่อยู่ของ SO เลขที่ ${this.soNumber} ได้`,
        `ที่อยู่ : ${soRecord.custAddress}`,
        `พื้นที่ที่จัดส่งได้ : ${areaLabel()}`,
        '',
        'ยืนยันว่าอยู่ในพื้นที่จัดส่งหรือไม่?'
      ],
      {
        type: 'confirm',
        color: 'warning',
        width: 450
      }
    );
  },

  // SO input Dialog
  inputSoOpen(booking) {
    if (!this.canBookHere) return;
    this.selectedBooking = booking;
    this.soNumber = (booking && booking.soNo)?booking.soNo:'' // Pre-fill existing SO if available
    // The SO typed here may not be the booking's, so its return is found from
    // SAP at confirm (fetchReturnFor), never carried over from the booking.
    this.rtNumber = '';
    this.returnOnly = false;
    this.soDialog = true;
  },
  async inputSoAddSoFromBooking() {
    try {
      // 1. Validate SO exists in MS SQL database
      const response = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, {
        so: this.soNumber
      });

      const detail = JSON.parse(response.data.d);
    
      // Check if we got valid array with at least one record
      if (!Array.isArray(detail) || detail.length === 0) {
        await this.$root.$message(
          'ไม่พบข้อมูล',
          'ไม่พบเลขที่ SO นี้ในระบบ',
          { 
            type: 'alert',
            color: 'error',
            width: 400
          }
        );
        return;
      }

      // Get first record (should be only one)
      const soRecord = detail[0];

      // Check SO has transferNo (already booking)
      // if (soRecord.transNo !== null || soRecord.CANCELED !== 'N') {
      if (soRecord.transNo !== null || soRecord.canceled !== 'N') {
        let errorMessage = '';
        
        if (soRecord.transNo !== null) {
          errorMessage = `เลขที่ SO นี้ถูกใช้งานโดยใบจัดรถเลขที่ ${soRecord.transNo}`;
        } 
        else if (soRecord.canceled !== 'N') {
          errorMessage = 'เลขที่ SO นี้ถูกยกเลิกแล้ว';
        }

        await this.$root.$message(
          'ไม่สามารถใช้เลขที่ SO นี้ได้',
          errorMessage,
          { 
            type: 'alert',
            color: 'error',
            width: 450
          }
        );
        return;
      }

      // Check the SO delivers somewhere the small car actually serves
      if (!await this.checkSoServiceArea(soRecord)) {
        return;
      }

      // Show confirmation with customer name and address
      const confirmProceed = await this.$root.$message(
        'พบข้อมูล SO',
        [
          `ลูกค้า: ${soRecord.custName||soRecord.cardName}`,
          `ที่อยู่: ${soRecord.custAddress}`,
          '',
          'ยืนยันการใช้เลขที่ SO นี้หรือไม่?'
        ],
        { 
          type: 'confirm',
          color: 'info',
          width: 450
        }
      );

      if (!confirmProceed) {
        return;
      }

      // 2. Check if SO is already used in another bookings
      const bookingQuery = await db.collection('booking')
        .where('soNo', '==', this.soNumber)
        .get();

      if (!bookingQuery.empty) {
        // Show confirmation to re-assign
        const confirm = await this.$root.$message(
          'เลขที่ SO มีอยู่แล้ว',
          [
            `เลขที่ ${this.soNumber} ถูกใช้งานโดยนัดหมายอื่นแล้ว`,
            `ต้องการย้ายมายังนัดหมายนี้หรือไม่?`
          ],
          { 
            type: 'confirm',
            color: 'warning'
          }
        );

        if (confirm) {
          // const result = await this.inputSoMove(bookingQuery);
          await this.inputSoMove(bookingQuery);
          
          // Update UI is handle by Event listener
          // Update UI with the moved booking info
          // if (result.success && result.removedBooking) {
          //   let result2 = this.findTimeSlotRecord(result.removedBooking.carName,result.removedBooking.date,result.removedBooking.slot,result.removedBooking.id)
          //   // set soNumber to current booking
          //   this.$set(this.selectedBooking,'soNo',this.soNumber)
          //   // clear soNo in the old booking
          //   this.$set(result2,'soNo',null)
          // }
        }
        return;
      }

      // 3. If valid and unused, proceed with update
      await this.inputSoUpdate();

    } catch (error) {
      reportError('SO update error:', error);
      await this.$root.$message(
        'เกิดข้อผิดพลาด',
        'ไม่สามารถอัปเดตเลขที่ SO',
        { 
          type: 'alert',
          color: 'error'
        }
      );
    } finally {
      this.soDialog = false;
    }
  },
  async inputSoAddSoNew(){
    this.transportFormMode = 'new' //set transform mode to 'new'
    try {
      // 1. Validate SO exists in MS SQL database
      const response = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, {
        so: this.soNumber
      });

      const detail = JSON.parse(response.data.d);
    
      // Check if we got valid array with at least one record
      if (!Array.isArray(detail) || detail.length === 0) {
        await this.$root.$message(
          'ไม่พบข้อมูล',
          [`ไม่พบ SO เลขที่ : ${this.soNumber} ในระบบ`,'โปรดตรวจสอบ !'],
          { 
            type: 'alert',
            color: 'error',
            width: 400
          }
        );
        return;
      }

      // Get first record (should be only one)
      const soRecord = detail[0];

      // Check SO was canceld or close
      if (soRecord.canceled === 'Y' || soRecord.status === 'C') {
        let errorMessage = '';
        
        if (soRecord.status === 'C') {
          errorMessage = `SO เลขที่ : ${this.soNumber} ถูกปิดแล้ว`;
        } 
        if (soRecord.canceled === 'Y') {
          errorMessage = `SO เลขที่ : ${this.soNumber} ถูกยกเลิกแล้ว`;
        }

        await this.$root.$message(
          'ไม่สามารถใช้เลขที่ SO นี้ได้',
          errorMessage,
          { 
            type: 'alert',
            color: 'error',
            width: 450
          }
        );
        return;
      }

      // Check the SO delivers somewhere the small car actually serves
      if (!await this.checkSoServiceArea(soRecord)) {
        return;
      }

      // Show confirmation with customer name and address
      const confirmProceed = await this.$root.$message(
        'พบข้อมูล SO',
        [
          `ลูกค้า: ${soRecord.custName||soRecord.cardName}`,
          `ที่อยู่: ${soRecord.custAddress}`,
          '',
          'ยืนยันการใช้เลขที่ SO นี้หรือไม่?'
        ],
        { 
          type: 'confirm',
          color: 'info',
          width: 450
        }
      );

      if (!confirmProceed) {
        return;
      }

      // 2. Check if SO has no Transport Number then update SO with Transport data
      if(!soRecord.transNo){
        this.newTransportForm(); //open transportForm with initail data
        return
      }

      // 3. if SO is already has Transfer number then ask user for change or not
      let deliveryDate = new Date(parseInt(soRecord.delDate.match(/\d+/)[0], 10)).toLocaleDateString('th-TH', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'});
      const confirm = await this.$root.$message(
        'SO นี้ถูกจัดรถแล้ว ',
        [
          `SO เลขที่ ${this.soNumber} ได้ถูกจัดรถแล้ว !`,
          `ใบจัดรถเลขที่ ${soRecord.transNo} วันที่ ${deliveryDate}`,
          `ต้องการย้ายมายังนัดหมายนี้หรือไม่?`
        ],
        { 
          type: 'confirm',
          color: 'warning'
        }
      );
      if(confirm){
        this.transportFormMode = 'move';
        this.newTransportForm(); //open transportForm with initail data
      }


    } catch (error) {
      reportError('SO update error:', error);
      await this.$root.$message(
        'เกิดข้อผิดพลาด',
        'ไม่สามารถอัปเดตเลขที่ SO',
        { 
          type: 'alert',
          color: 'error'
        }
      );
    } finally {
      this.soDialog = false;
    }
  },
  async inputSoDelete() {
    try {
      // Check if selectedBooking exists and has an ID
      if (!this.selectedBooking || !this.selectedBooking.id) {
        throw new Error('No booking selected');
      }

      // Ask for confirmation
      const confirm = await this.$root.$message(
        'ยืนยันการลบ',
        'คุณแน่ใจที่จะลบเลขที่ SO ออกจากนัดหมายนี้หรือไม่?',
        { 
          type: 'confirm',
          color: 'warning'
        }
      );

      if (confirm) {
        // Set soNo to null in booking collection and selectedBooking
        await db.collection('booking').doc(this.selectedBooking.id).update({
          soNo: null,
          bookingUpdated: new Date()
        });
        
        // Update local data
        this.$set(this.selectedBooking, 'soNo', null);
        this.soNumber = '';
        this.rtNumber = '';
        this.returnOnly = false;
        
        await this.$root.$message(
          'ลบสำเร็จ',
          'ลบเลขที่ SO เรียบร้อยแล้ว',
          { 
            type: 'alert',
            color: 'success'
          }
        );
      }
    } catch (error) {
      reportError('Delete SO error:', error);
      await this.$root.$message(
        'เกิดข้อผิดพลาด',
        'ไม่สามารถลบเลขที่ SO',
        { 
          type: 'alert',
          color: 'error'
        }
      );
    } finally {
      this.soDialog = false;
    }
  },
  async inputSoMove(bookingQuery) {
    const batch = db.batch();
    let removedBookingInfo = null;

    // 1. Get document info before removal
    if (!bookingQuery.empty) {
      const doc = bookingQuery.docs[0];
      removedBookingInfo = {
        id: doc.id,
        date: doc.data().date,  // Make sure your booking documents have a 'date' field
        // Add other fields you might need for UI updates
        ...doc.data()
      };
    }

    // 2. Remove SO from existing booking(s)
    bookingQuery.forEach(doc => {
      const docRef = db.collection('booking').doc(doc.id);
      batch.update(docRef, { soNo: null });
    });

    // 3. Assign SO to current booking
    const currentRef = db.collection('booking').doc(this.selectedBooking.id);
    batch.update(currentRef, {
      soNo: this.soNumber,
      bookingUpdated: new Date()
    });

    await batch.commit();
    
    return {
      success: true,
      removedBooking: removedBookingInfo,
      message: 'ย้ายเลขที่ SO เรียบร้อยแล้ว'
    };
  },
  async inputSoUpdate() {
    await db.collection('booking')
      .doc(this.selectedBooking.id)
      .update({ soNo: this.soNumber });
    
    // UI update handle by EventListener
    // this.$set(this.selectedBooking,'soNo',this.soNumber)

    await this.$root.$message(
      'สำเร็จ',
      'บันทึกเลขที่ SO เรียบร้อย',
      { 
        type: 'alert',
        color: 'success'
      }
    );
  },

  // Detail dialog
  approveBooking(booking,index){
    // Set transportFormMode
    this.transportFormMode = 'approve'

    // keep booking and index
    this.selectedBooking = booking;
    this.selectedSlotBookingsIndex = index;

    // keep so number for using in transportForm
    this.soNumber = booking.soNo;
    // ...and the return the job carries: an exchange collects it beside the SO,
    // a return-only job (soNo null) has nothing else to deliver.
    this.rtNumber = booking.rtNo || '';
    // The same test that enables ยืนยัน (an RT and no SO), so the two cannot disagree.
    this.returnOnly = !booking.soNo && !!booking.rtNo;

    // initalize and open transportForm
    this.newTransportForm();
  },
  // async approveBooking(booking,index) {
  async newTransportForm() {
    
    try {
      // if slot has no transport number then get new one
      if (!this.selectedSlot.transNo) {
        // Step 1: Get last transfer number from database
        const response = await axios.post(
          this.webPath + 'deliveryWebService.asmx/GetTransNoLast', 
          {}
        );
        
        // Parse the response to get the last transfer number
        const lastTransNo = JSON.parse(response.data.d).transNoLast;
        
        // Convert to number, increment, then convert back to string
        // Handle cases where lastTransNo might be null or empty
        const numericPart = lastTransNo ? parseInt(lastTransNo.toString().replace(/\D/g, '')) || 0 : 0;
        const newTransNo = (numericPart + 1).toString();
        
        // Step 2: Confirm with user about creating new transfer number
        const confirmCreate = await this.$root.$message(
          'สร้างเลขที่ใบจัดรถใหม่',
          [
            'ตารางนัดหมายวันนี้ ยังไม่มีเลขที่ใบจัดส่ง',
            '',
            // `เลขที่ใบจัดรถล่าสุด: ${lastTransNo}`,
            `ต้องการสร้างเลขที่ใบจัดรถใหม่: ${newTransNo} ?`,
            // '',
            // 'ยืนยันการสร้างเลขที่ใบจัดรถใหม่หรือไม่?'
          ],
          { 
            type: 'confirm',
            color: 'info',
            width: 450
          }
        );
        
        if (confirmCreate) {
          // User confirmed - set the new transfer number
          // this.$set(this.selectedSlot, 'transNo', newTransNo);
          this.selectedSlotNewTransNo = newTransNo
        } else {
          // Step 3: User didn't confirm - exit
          return;
        }
      }else{
        this.selectedSlotNewTransNo = this.selectedSlot.transNo;
      }
      
      // Transport dialog handle

      // 1. Initialize transport form data
      //
      // เริ่มส่งเวลา / สิ้นสุดส่งเวลา are seeded from the booking's own `time`
      // when it has one. A shop books a half-day (AM/PM) and carries no clock
      // time, so the fields stay blank exactly as before -- but a booking the
      // planner sent through dispatchPlan carries `time: '08:00 - 08:30'`
      // (dispatchPlan.toBookingDocs), which is the window the route was
      // sequenced around. This dialog was rebuilding the object from scratch
      // and dropping it, so staff saw two empty fields and the approved job
      // went into deliveryList with the half-day slot instead of the planned
      // window -- the planner's arithmetic thrown away at the confirm step.
      //
      // editTransportForm parses the same field the same way; getDeliveryTime()
      // below already prefers startTime/endTime over the slot, so seeding the
      // form is all that is needed for the window to reach deliveryList.
      const bookingTime = this.splitTimeRange(this.selectedBooking && this.selectedBooking.time);

      this.transportForm = {
        carNumber: this.selectedCarName,
        driverCode: this.selectedSlot.driverCode?this.selectedSlot.driverCode:this.cars[this.selectedCarIndex].driverCode,
        driverName: this.selectedSlot.driverName || '',
        deliveryDate: this.selectedSlot.deliveryDate,
        deliveryTime: this.selectedSlotTime, //AM PM OM
        loadDate: this.selectedSlot.loadDate,
        startTime: bookingTime.startTime,
        endTime: bookingTime.endTime
      };

      // 2. Open transportForm dialog
      // Load driver data if need
      if(this.driverLst.length == 0){
        await this.getActiveDrivers();
      }
      this.showTransportDialog = true;

    } catch (error) {
      reportError('Error in approveBooking:', error);
      this.$root.$message(
        'เกิดข้อผิดพลาด',
        ['ไม่สามารถดำเนินการได้ในขณะนี้'],
        { type: 'error' }
      );
    }
  },
  // --- พิกัด / จุดสังเกต --------------------------------------------------------
  // A pin is the only place a driver's นำทาง goes to directly (driverDay.mapsUrl);
  // without one it hands Google the free-text address. Jobs added through
  // เพิ่ม SO never carried one, so this dialog is where they get it. Every role
  // that can open this page may set it, shops included, and every save records
  // who did. The dialog itself is listPinDialog.vue, shared with จัดรถ.
  hasPin(row) {
    return !!R.parseGeo(row && row.geo);
  },
  openPinDialog(row) {
    this.pinDialog = { open: true, row };
  },
  pinAddress(row) {
    if (!row) return '';
    return ((row.address ? row.address + ' ' + this.formatThaiLocation(row.location) : row.custAddress) || '').trim();
  },

  async deleteBooking(booking) {
    if (!this.canBookHere) return;
    // THE FIFTH DELETE SITE. A row with status 'SO' is a `deliveryList`
    // document -- the branch below deletes it and its `deliveryTimeline`
    // document -- so it is a job a driver may already have stamped, and it
    // needs the same guard as managecar.vue's four delete dialogs. A plain
    // booking has no deliveryList row, has never reached the driver app, and
    // is checked for nothing.
    //
    // The guard warns; it never refuses. Deleting a postponed job is right
    // even after the driver has set off. Deleting one the driver has already
    // FINISHED destroys the only record that the delivery happened, and that
    // is what the red dialog is for.
    const guard = RJ.isListRow(booking)
      ? await driverService.stampGuardForListDoc(booking.id)
      : null;
    const guardNote = guard ? driverDay.deleteGuardNote(guard) : '';
    const lines = [
      `คุณต้องการลบการจองรถเพื่อส่ง: ${booking.name || booking.custName}`,
      'ต้องการดำเนินการต่อหรือไม่?'
    ];
    // message.vue renders '' as a line break.
    if (guardNote) lines.push('', guardNote);

    const confirm = await this.$root.$message(
      'ยืนยันการลบการจองรถ',
      lines,
      { 
        type: 'confirm',
        color: guard && guard.finished ? 'error' : 'warning'
      }
    );
    if (!confirm) return;

    try {
      if (!RJ.isListRow(booking)) {
        // Simple booking deletion
        await db.collection("booking").doc(booking.id).delete();
      } else {
        // Start batched write for atomic operations
        const batch = db.batch();
        
        // 1. Cancel SO in SAP first (most critical)
        if (booking.soNo) {
          await axios.post(this.webPath + 'deliveryWebService.asmx/CancelSo', {
            soNo: booking.soNo
          });
        }
        // The return leaves the sheet with its visit, as managecar's delete does --
        // but only an RT this page put there (a return row, or one stamped
        // rtDocEntry by UpdateRT). A row written before returns existed may carry
        // a copied rtNo for an RT dispatched separately on จัดรถ; clearing that
        // would pull someone else's job off its sheet.
        if (booking.rtNo && (booking.status === 'RT' || booking.rtDocEntry)) {
          await axios.post(this.webPath + 'deliveryWebService.asmx/CancelRt', {
            rtNo: booking.rtNo
          });
        }

        // 2. Add all Firestore deletions to batch
        if (booking.id) {
          const deliveryListRef = db.collection("deliveryList").doc(booking.id);
          batch.delete(deliveryListRef);
        }
        
        const timelineQuery = await db.collection("deliveryTimeline")
          .where("trackNo", "==", booking.soNo || booking.rtNo)
          .get();
          
        if (timelineQuery.docs[0]) {
          batch.delete(timelineQuery.docs[0].ref);
        }

        // 3. Execute all Firestore deletions atomically
        await batch.commit();
      }

      // Update UI
      // this.selectedSlotBookings.splice(index, 1);
      
      await this.$root.$message(
        'ลบการจองสำเร็จ',
        'ระบบได้ลบการจองรถเรียบร้อยแล้ว',
        { 
          type: 'alert',
          color: 'success',
          width: 450
        }
      );

    } catch (error) {
      reportError("Delete failed:", error);
      await this.$root.$message(
        'ลบการจองไม่สำเร็จ',
        `เกิดข้อผิดพลาด: ${error.message || 'โปรดติดต่อฝ่าย IT Kiosk'}`,
        { 
          type: 'alert',
          color: 'error',
          width: 450
        }
      );
    }
  },
  printDelivery(transNo){
    if(!transNo){
      return
    }
    let routeData = this.$router.resolve({name: 'deliveryReport', query: {
        transNo: transNo
    }})
    window.open(routeData.href, "_blank")
  },
  
  async toggleLock() {
    try {
      if(!(this.isAdmin() || this.userRole==='delivery')) return;
      if (!this.selectedSlot) return;
      
      // const date = this.selectedSlot.deliveryDate;
      const date = this.selectedSlot.deliveryDate;
      const regNum = this.selectedCarName;
      
      if (!date || !regNum) {
        reportError('Missing required fields: date or regNum');
        return;
      }

      // Get reference to the locked document
      const docRef = db.collection('locked').doc(`${date}_${regNum}`);

      if (this.selectedSlotTime === null) {
        // Toggle both AM and PM slots based on current locked state
        if (this.isSlotLocked) {
          // Unlock both slots
          await docRef.set({
            date: date,
            regNum: regNum,
            am: false,
            pm: false
          }, { merge: true });
          
          // Update local state
          // this.selectedSlot.lockedAm = false;
          // this.selectedSlot.lockedPm = false;
        } else {
          // Lock both slots
          await docRef.set({
            date: date,
            regNum: regNum,
            am: true,
            pm: true
          }, { merge: true });
          
          // Update local state
          // this.selectedSlot.lockedAm = true;
          // this.selectedSlot.lockedPm = true;
        }
      } else if (this.selectedSlotTime === 'AM') {
        // Toggle only AM slot
        const newAmValue = !this.selectedSlot.lockedAm;
        
        await docRef.set({
          date: date,
          regNum: regNum,
          am: newAmValue
        }, { merge: true });
        
        // Update local state
        // this.selectedSlot.lockedAm = newAmValue;
      } else if (this.selectedSlotTime === 'PM') {
        // Toggle only PM slot
        const newPmValue = !this.selectedSlot.lockedPm;
        
        await docRef.set({
          date: date,
          regNum: regNum,
          pm: newPmValue
        }, { merge: true });
        
        // Update local state
        // this.selectedSlot.lockedPm = newPmValue;
      }

      // Force UI update
      this.$forceUpdate();
    } catch (error) {
      reportError('Error toggling lock:', error);
    }
  },


  // Group of methods for transportForm dialog
  async editTransportForm(booking,index){
    // Set transportFormMode
    this.transportFormMode = 'edit'

    // keep booking and index
    this.selectedBooking = booking;
    this.selectedSlotBookingsIndex = index;

    // keep so Number for using with transportForm
    this.soNumber = booking.soNo;
    this.rtNumber = booking.rtNo || '';
    this.returnOnly = booking.status === 'RT';

    // Prepare data for TransportForm
    this.selectedSlotNewTransNo = booking.transNo;
    this.$set(this.transportForm,'carNumber',booking.regNum)
    this.$set(this.transportForm, 'driverCode', (booking.driver && booking.driver.split('-')[0] || '').trim());
    this.$set(this.transportForm,'loadDate',booking.loadDate)
    this.$set(this.transportForm,'deliveryDate',booking.deliveryDate)
    const timeRange = this.splitTimeRange(booking.time);
    this.$set(this.transportForm, 'startTime', timeRange.startTime);
    this.$set(this.transportForm, 'endTime', timeRange.endTime);

    // open TransportForm
    this.showTransportDialog = true;
  },
  async onclickOkTransportForm() {
    switch (this.transportFormMode) {
      case 'edit':
        this.submitTransportFormEdit();
        break;
      case 'move':
        this.submitTransportFormMove();
        break;
      case 'approve':
      case 'new':
      default:
        this.submitTransportForm();
        break;
    }
  },
  // transportFormMode = 'approve' or 'new'
  async submitTransportForm() {
    try {
      // Validate form
      this.validateTransportForm();

      this.showDialogProgress = true;
      let rtWarning = '';

      if (this.returnOnly) {
        // A pickup alone: the RT is the job -- a deliveryList row of status 'RT'.
        const ret = await this.fetchReturnFor({ rtNo: this.rtNumber, returnOnly: true });
        const payMent = await this.fetchPayment(ret.rt.typePayment);
        await this.updateRtToSAP(ret.rt.docEntry);
        await this.addReturnToFirestore(ret, payMent);
      } else {
        // Get so , products and payment
        const soData = await this.fetchSoData(this.soNumber);
        const payMent = await this.fetchPayment(soData.typePayment);
        const prodList = await this.fetchProducts(this.soNumber);
        const docEntry = prodList[0].docEntry
        // Read before writing anything, so a SAP error here stops the confirm
        // with nothing half-done.
        const ret = await this.fetchReturnFor({ soData, prodList, rtNo: this.rtNumber, returnOnly: false });
        // update transport data to SO (and its RT) in SAP
        await this.updateSoToSAP(docEntry);
        // The SO now carries the transNo, so fetchSoData would refuse a retry:
        // a failed UpdateRT from here on is reported, never allowed to strand
        // the SO without its deliveryList row.
        if (ret) {
          try {
            await this.updateRtToSAP(ret.rt.docEntry);
          } catch (error) {
            reportError(`UpdateRT ${ret.rt.rtNo} failed after UpdateSo; job written anyway`, error);
            rtWarning = `บันทึกการจัดรถแล้ว แต่ยังอัปเดต RT ${ret.rt.rtNo} ใน SAP ไม่สำเร็จ โปรดแก้ไขใน SAP`;
          }
        }
        // add transport data to Firestore
        await this.addDeliveryToFirestore(soData, prodList, payMent, ret);
      }

      // delete current Booking
      if(this.transportFormMode === 'approve'){
        await db.collection("booking").doc(this.selectedBooking.id).delete();
      }

      this.showDialogProgress = false;

      // show success
      await this.$root.$message(
        'บันทึกนัดหมายสำเร็จ',
        'บันทึกข้อมูล SO เข้าสู่ตารางนัดหมายเรียบร้อย',
        { 
          type: 'alert',
          color: 'success'
        }
      );
      if (rtWarning) {
        await this.$root.$message('อัปเดต RT ใน SAP ไม่สำเร็จ', rtWarning, { type: 'alert', color: 'warning' });
      }

      // close transport dialog
      this.showTransportDialog = false;
    } catch (error) {
      reportError('Submission error:', error);
      
      const errorTitle = error.type === 'validation' 
        ? 'ข้อมูลไม่ครบถ้วน' 
        : 'เกิดข้อผิดพลาด';
      
      this.showDialogProgress = false;
      this.$root.$message(errorTitle, error.message, {
        type: 'alert',
        // duration: error.type === 'so' ? 5000 : 3000 // Longer duration for SO errors
      });
    }
  },
  // transportFormMode = 'edit'
  async submitTransportFormEdit(){
    try {
      // Validate form
      this.validateTransportForm();

      this.showDialogProgress = true;

      if (this.selectedBooking.status === 'RT') {
        // A return-only row has no SO and no products: the RT is what moves.
        // A row written before rtDocEntry existed is looked up in SAP.
        let rtDocEntry = this.selectedBooking.rtDocEntry;
        if (!rtDocEntry) {
          rtDocEntry = await this.rtDocEntryFor(this.selectedBooking.rtNo);
        }
        await this.updateRtToSAP(rtDocEntry);
      } else {
        // check product list in the booking
        if(!this.selectedBooking.prodList || this.selectedBooking.prodList.length === 0)throw {
          type: 'so',
          message: ['ไม่พบรายการสินค้าใน SO นี้','โปรดตรวจสอบ']
        };

        // set docEntry
        const docEntry = this.selectedBooking.prodList[0].docEntry

        // update transport data to SO in SAP, and the exchange's RT with it
        await this.updateSoToSAP(docEntry);
        if (this.selectedBooking.rtDocEntry) await this.updateRtToSAP(this.selectedBooking.rtDocEntry);
      }

      // update transport data in Firestore
      // const deliveryData = await this.updateDeliveryToFirestore();
      await this.updateDeliveryToFirestore();
      
      this.showDialogProgress = false;

      // show success
      await this.$root.$message(
        'บันทึกข้อมูลสำเร็จ',
        'บันทึกการแก้ไขข้อมูล SO เข้าสู่ตารางนัดหมายเรียบร้อยแล้ว',
        { 
          type: 'alert',
          color: 'success'
        }
      );
      
      // close transport dialog
      this.showTransportDialog = false;
    } catch (error) {
      reportError('Submission error:', error);
      this.showDialogProgress = false;
      
      const errorTitle = error.type === 'validation' 
        ? 'ข้อมูลไม่ครบถ้วน' 
        : 'เกิดข้อผิดพลาด';
        
      this.$root.$message(errorTitle, error.message, {
        type: 'alert',
        // duration: error.type === 'so' ? 5000 : 3000 // Longer duration for SO errors
      });
    }
  },
  // transportFormMode = 'move'
  async submitTransportFormMove(){
    try {
      // Validate form
      this.validateTransportForm();

      this.showDialogProgress = true;

      // Get products list for docEntry 
      const prodList = await this.fetchProducts(this.soNumber);
      
      // check product list
      if(!prodList || prodList.length === 0)throw {
        type: 'so',
        message: ['ไม่พบรายการสินค้าใน SO นี้','โปรดตรวจสอบ']
      };
      
      // set docEntry
      const docEntry = prodList[0].docEntry

      // update transport data to SO in SAP
      await this.updateSoToSAP(docEntry);

      // update transport data in Firestore
      const moved = await this.updateDeliveryToFirestore();
      // An exchange's return rides on the same row, so it moves with it.
      if (moved.rtDocEntry) await this.updateRtToSAP(moved.rtDocEntry);
      
      this.showDialogProgress = false;
      // show success
      await this.$root.$message(
        'บันทึกข้อมูล SO สำเร็จ',
        'ทำการย้าย SO เข้าสู่ตารางนัดหมายเรียบร้อยแล้ว',
        { 
          type: 'alert',
          color: 'success'
        }
      );
      
      // close transport dialog
      this.showTransportDialog = false;
    } catch (error) {
      reportError('Submission error:', error);
      this.showDialogProgress = false;
      
      const errorTitle = error.type === 'validation' 
        ? 'ข้อมูลไม่ครบถ้วน' 
        : 'เกิดข้อผิดพลาด';
        
      this.$root.$message(errorTitle, error.message, {
        type: 'alert',
        // duration: error.type === 'so' ? 5000 : 3000 // Longer duration for SO errors
      });
    }
  },

  // Group of methods for TransportForm
  // Helper group
  _prepareTime(){
    let time = this.selectedBooking?this.selectedBooking.slot:null;

    // if selectedSlotTime is 'AM' or 'PM', set time to this.selectedSlotTime
    if (this.selectedSlotTime === 'AM' || this.selectedSlotTime === 'PM') {
      time = this.selectedSlotTime;
    }

    if(this.transportForm.startTime && this.transportForm.endTime){
      time = this.transportForm.startTime + " - " + this.transportForm.endTime
    }
    return time || null
  },
  // The sheet these rows are written onto -- the transport form, and the
  // trip's last day when the booking came from a 2-day route (sendTripToDispatch
  // stamps it), so the driver app keeps the job on day 2.
  _sheet () {
    return {
      transNo: this.selectedSlotNewTransNo,
      loadDate: this.transportForm.loadDate,
      deliveryDate: this.transportForm.deliveryDate,
      regNum: this.transportForm.carNumber,
      driver: this.selectedDriverCodeAndName,
      driverName: this.selectedDriverName || '',
      tripEnd: (this.selectedBooking && this.selectedBooking.tripEnd) || null
    };
  },
  // A stop of a 2-day route carries its day and its place in the route
  // (dispatchPlan.tripStopFields); copied onto the row, it is what splits
  // จัดรถ, ใบขนส่ง and the driver's day. null on every other job, which
  // DR.listRow then leaves off.
  _plan () {
    const b = this.selectedBooking || {};
    // planDateWas: a day-2 stop moved to day 1 by แก้จำนวนวัน, kept so 1 -> 2
    // can put it back (DR.daysChange).
    return { planDate: b.planDate || null, planSeq: b.planSeq == null ? null : b.planSeq, planDateWas: b.planDateWas || null };
  },
  // Validate group
  validateTransportForm(){
    const timeFieldsValid = (
      (!this.transportForm.startTime && !this.transportForm.endTime) || 
      this.validateTimeRange()
    );
    
    if (!this.$refs.transportForm.validate() || !timeFieldsValid) {
      throw { 
        type: 'validation',
        message: ['กรุณาตรวจสอบข้อมูล']
      };
    }
  },
  // 'HH:mm - HH:mm' -> the two ends, blank for anything else. A booking with no
  // clock time (a shop's AM/PM) yields two empty strings, which is what leaves
  // the fields blank and lets getDeliveryTime() fall back to the slot.
  splitTimeRange(time) {
    const parts = (time && String(time).split('-')) || [];
    return {
      startTime: (parts[0] || '').trim(),
      endTime: (parts[1] || '').trim()
    };
  },
  validateTimeRange() {
    if (!this.transportForm.startTime || !this.transportForm.endTime) return true;
    
    const start = this.transportForm.startTime.replace(':', '');
    const end = this.transportForm.endTime.replace(':', '');
    return end > start;
  },
  // API group
  async fetchSoData(soNo) {
    try {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`, {
        so: soNo 
      });
      const data = JSON.parse(res.data.d);
      
      if (!data.length) throw {
        type: 'so',
        message: ['ไม่พบเลขที่ SO นี้ในระบบ']
      };
      
      const soData = data[0];
      
      // SO Validation Checks
      if (soData.canceled === 'Y') throw {
        type: 'so',
        message: [
          'เลขที่ SO นี้ถูกยกเลิกแล้ว',
          'ไม่สามารถยืนยันนัดหมายในตารางจัดส่งได้',
          'โปรดแก้ไขเลขที่ SO ให้ถูกต้อง'
        ]
      };
      
      if (soData.status === 'C') throw {
        type: 'so',
        message: [
          'เลขที่ SO นี้ถูกปิดแล้ว',
          'ไม่สามารถยืนยันนัดหมายในตารางจัดส่งได้',
          'โปรดแก้ไขเลขที่ SO ให้ถูกต้อง'
        ]
      };
      
      if (soData.transNo) throw {
        type: 'so',
        message: [
          `เลขที่ SO นี้มีเลขที่การจัดรถแล้ว ใบจัดรถเลขที่ : ${soData.transNo}`,
          'ไม่สามารถยืนยันนัดหมายในตารางจัดส่งได้',
          'โปรดแก้ไขเลขที่ SO ให้ถูกต้อง'
        ]
      };
      
      return soData;
      
    } catch (error) {
      throw { type: 'so', message: ['ไม่สามารถดึงข้อมูล SO จากระบบ SAP ได้',
          'Error : deliveryWebService.asmx/GetSo',
          error.message
        ]
      };
    }
    
  },
  async fetchPayment(typePayment) {
    if (!typePayment) return '';
    try {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetPayment`, {
        typePayment: typePayment
      });
      const paymentData = JSON.parse(res.data.d);
      return (paymentData[0] && paymentData[0].payment) || '';
    } catch (error) {
      reportWarning('Payment data fetch failed, using empty string', error);
      return ''; // Graceful fallback
    }
        
  },
  async fetchProducts(soNo){
    try {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetProdList`, {
        so: soNo
      });
      const products = JSON.parse(res.data.d);
      if (!products.length) throw {
        type: 'product',
        message: [
          'ไม่พบรายการสินค้าในการจัดส่ง',
          'โปรดแจ้งผู้ดูแลระบบ'
        ]
      };
      return products;
    } catch (error) {
      throw { type: 'product', message: ['ไม่สามารถดึงข้อมูลสินค้าได้',
        'Error : deliveryWebService.asmx/GetProdList',
        error
      ] };
    }
    
  },
  async updateSoToSAP(docEntry) {
    try {
      // Safely prepare time
      let time = null;
      if (this._prepareTime) {
        try {
          time = this._prepareTime();
        } catch (e) {
          reportWarning('Time preparation failed:', e);
        }
      }

      // Safely prepare payload
      const transportForm = this.transportForm || {};
      const payload = {
        docEntry: docEntry,
        transNo: this.selectedSlotNewTransNo || null,
        loadDate: transportForm.loadDate || null,
        delDate: transportForm.deliveryDate || null,
        driver: transportForm.driverCode || null,
        regNum: transportForm.carNumber || null,
        serviceTime: time || null
      };

      const response = await axios.post(
        `${this.webPath}deliveryWebService.asmx/UpdateSo`,
        JSON.stringify(payload),
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8'
          },
          timeout: 10000
        }
      );

      // Handle ASMX response
      const result = response.data && response.data.d;
      if (result !== "true") {
        throw new Error('SAP returned failure status');
      }

      return true;
    } catch (error) {
      reportError('UpdateSo Failed:', {
        error: error.message,
        response: error.response && error.response.data,
        stack: error.stack
      });

      throw {
        type: 'updateSo',
        message: [
          'ไม่สามารถอัปเดตข้อมูลในระบบ SAP ได้',
          'Error : deliveryWebService.asmx/UpdateSo',
          (error.response && error.response.data && error.response.data.message) || error.message
        ],
        details: error.response && error.response.data
      };
    }
  },

  // The return a job carries, read from SAP now: the SO's U_KSK_ReturnNo, else
  // the booking's own rtNo, else -- only when neither is known -- the newest
  // open RT of the SO's ORIGINAL sale (GetRtBySo with the product rows' saleSo;
  // an RT points at the original sale, never at the new SO), so an RT linked
  // after the booking was made still reaches the truck. A guess never replaces
  // a known RT (RJ.knownRt). SAP wins over the booking, as for the pin.
  // Returns null when there is none; throws (stopping the confirm) only for a
  // return-only job, which has nothing else to deliver. A failed read throws
  // for both: nothing has been written yet, so stopping is the safe answer.
  async fetchReturnFor({ soData, prodList, rtNo, returnOnly }) {
    let chosen = rtNo ? { rtNo: String(rtNo), others: [] } : null;
    if (!returnOnly && soData) {
      const known = RJ.knownRt({ soRtNo: soData.rtNo, bookingRtNo: rtNo });
      let bySo = [];
      const saleSo = known ? '' : RJ.saleSoOf(prodList);
      if (saleSo) {
        try {
          const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRtBySo`, { so: Number(saleSo) });
          bySo = JSON.parse(res.data.d);
        } catch (e) { reportWarning('GetRtBySo failed at confirm', e); }
      }
      chosen = RJ.pickRt(known ? known.rtNo : '', bySo);
    }
    if (!chosen) return null;
    // One return, one booking: an RT another live bookingSlot holds is not
    // stamped onto this job too (the guard bookingDate and upcountry run). The
    // job's own slot, or a job already carrying this RT, is not "another".
    let holder;
    try {
      holder = await S.findActiveBookingForRt(chosen.rtNo);
    } catch (error) {
      throw { type: 'so', message: [`ตรวจสอบการจองของ RT ${chosen.rtNo} ไม่ได้`, 'โปรดลองใหม่', (error && error.message) || ''] };
    }
    const job = this.selectedBooking || {};
    if (RJ.rtHeldElsewhere(holder, { sourceSlotId: job.sourceSlotId, ownRtNo: job.rtNo, rtNo: chosen.rtNo })) {
      if (returnOnly) throw { type: 'so', message: [`RT ${chosen.rtNo} มีการจองอื่นอยู่แล้ว (${RJ.refText(holder)})`, 'ไม่สามารถยืนยันนัดหมายในตารางจัดส่งได้'] };
      reportWarning(`RT ${chosen.rtNo} skipped at confirm: held by bookingSlot ${holder.id}`);
      return null;
    }
    if (!chosen) return null;
    let rt, items;
    try {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRT`, { rtNo: Number(chosen.rtNo) });
      const rows = JSON.parse(res.data.d);
      rt = rows && rows[0];
      const problem = rt ? RJ.rtProblem(rt) : 'CANCELLED';
      if (problem) {
        if (returnOnly) throw { type: 'so', message: [`RT ${chosen.rtNo}: ${rt ? RJ.rtProblemText(rt) : RJ.RT_PROBLEM_TH.CANCELLED}`, 'ไม่สามารถยืนยันนัดหมายในตารางจัดส่งได้'] };
        reportWarning(`RT ${chosen.rtNo} skipped at confirm: ${problem}`);
        return null;
      }
      const prod = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRtProdList`, { docEntry: rt.docEntry });
      items = JSON.parse(prod.data.d);
    } catch (error) {
      if (error && error.type) throw error;
      throw { type: 'so', message: [`ไม่สามารถดึงข้อมูล RT ${chosen.rtNo} จากระบบ SAP ได้`,
        'Error : deliveryWebService.asmx/GetRT , GetRtProdList',
        (error && error.message) || ''
      ] };
    }
    // rtNo on every line: deliveryReport.vue prints item.rtNo per return line.
    return { rt, rows: (Array.isArray(items) ? items : []).map(r => Object.assign({}, r, { rtNo: String(chosen.rtNo) })) };
  },
  // docEntry of an RT already on a sheet (a row managecar's addRt wrote has no
  // rtDocEntry). Such an RT is dispatched by definition, so unlike
  // fetchReturnFor this refuses only a missing or cancelled RT.
  async rtDocEntryFor(rtNo) {
    let rt;
    try {
      const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetRT`, { rtNo: Number(rtNo) });
      const rows = JSON.parse(res.data.d);
      rt = rows && rows[0];
    } catch (error) {
      throw { type: 'so', message: [`ไม่สามารถดึงข้อมูล RT ${rtNo} จากระบบ SAP ได้`, 'Error : deliveryWebService.asmx/GetRT', (error && error.message) || ''] };
    }
    if (!rt) throw { type: 'so', message: [`ไม่พบเลขที่ RT ${rtNo} ในระบบ`, 'โปรดตรวจสอบ'] };
    if (rt.canceled === 'Y') throw { type: 'so', message: [`RT ${rtNo}: ${RJ.RT_PROBLEM_TH.CANCELLED}`, 'ไม่สามารถแก้ไขการจัดส่งได้'] };
    return rt.docEntry;
  },
  // UpdateSo's twin for the RT (ORDN). UpdateRT answers "Success", not "true",
  // on success (deliveryWebService.asmx.cs), so both are accepted.
  async updateRtToSAP(docEntry) {
    let time = null;
    try {
      time = this._prepareTime();
    } catch (e) {
      reportWarning('Time preparation failed:', e);
    }
    const t = this.transportForm || {};
    let res;
    try {
      res = await axios.post(`${this.webPath}deliveryWebService.asmx/UpdateRT`, {
        docEntry: docEntry,
        transNo: this.selectedSlotNewTransNo || null,
        loadDate: t.loadDate || null,
        delDate: t.deliveryDate || null,
        driver: t.driverCode || null,
        regNum: t.carNumber || null,
        serviceTime: time || null
      }, { timeout: 10000 });
    } catch (error) {
      reportError('UpdateRT failed:', error);
    }
    const d = res && res.data && res.data.d;
    if (d !== 'Success' && d !== 'true') {
      throw { type: 'updateSo', message: ['ไม่สามารถอัปเดตข้อมูล RT ในระบบ SAP ได้', 'Error : deliveryWebService.asmx/UpdateRT'] };
    }
  },
  // A return-only job: the deliveryList + deliveryTimeline pair managecar's
  // addRt writes, in one batch, with the pin (SAP first, the booking second).
  async addReturnToFirestore(ret, payMent) {
    try {
      const time = this._prepareTime();
      const rt = ret.rt;
      const pin = R.soPin(rt);
      const job = {
        status: 'RT',
        cardCode: rt.cardCode,
        cardPhone: [rt.phone1, rt.phone2, rt.phone3].filter(Boolean).join(' '),
        cardName: rt.cardName,
        custName: rt.custName || '',
        custTel: (rt.custTel || '').replace(/-/g, ""),
        timelineTel: rt.custTel || '',
        custAddress: rt.custAddress || '',
        geo: pin.geo || (this.selectedBooking && this.selectedBooking.geo) || '',
        landmark: pin.landmark || (this.selectedBooking && this.selectedBooking.landmark) || '',
        prodList: [],
        prodListRt: ret.rows,
        soNo: '',
        rtNo: String(rt.rtNo),
        rtDocEntry: rt.docEntry,
        city: rt.city || '',
        payMent: payMent,
        transferAmt: rt.transferAmt || null,
        time: time
      };
      const sheet = this._sheet();
      Object.assign(job, this._plan());
      const stampAt = new Date().toISOString().slice(0,10) + " " + new Date().toTimeString().slice(0,8);
      const batch = db.batch();
      batch.set(db.collection("deliveryList").doc(), DR.listRow(job, sheet, stampAt));
      batch.set(db.collection("deliveryTimeline").doc(), DR.timelineDoc(job, sheet, stampAt));
      await batch.commit();
    } catch (error) {
      throw {
        type: 'firestore',
        message: ['ไม่สามารถบันทึกข้อมูลการจัดส่งได้',
          'Add to firestore collection error : deliveryList , deliveryTimeline',
          error
        ]
      };
    }
  },
  async addDeliveryToFirestore (soData,prodList,payMent,ret){
    try {
      // Prepare service time string
      let time = this._prepareTime();
      // Validated: a coordinate SAP cannot be read as one is dropped, not passed
      // on to a driver.
      const soPin = R.soPin(soData);

      const job = {
        status: 'SO',
        cardCode: soData.cardCode,
        cardPhone: [soData.phone1, soData.phone2, soData.phone3].filter(Boolean).join(' '),
        cardName: soData.cardName,
        custName: soData.custName || '',
        custTel: (soData.custTel || '').replace(/-/g, ""), // remove '-' from telephone number
        timelineTel: soData.custTel || '',
        custAddress: soData.custAddress || '',
        // SAP first, the booking second. This is the last hop before the driver
        // app, and the SO was re-read a moment ago (fetchSoData), so SAP holds
        // the newer value whenever the two disagree: a shop correcting a
        // customer's พิกัด in the order system writes it straight to ORDR, days
        // after the delivery date was booked here. The booking still answers for
        // a field SAP is blank on -- blank means "not recorded" there, and a pin
        // dropped by hand in this app is usually the only one anyone has.
        // Without either, every stop resolves to a district centroid, which is a
        // clustering hint for the ETA chain and never a navigation target, so
        // นำทาง falls back to handing Google the free-text address.
        geo: soPin.geo || (this.selectedBooking && this.selectedBooking.geo) || '',
        landmark: soPin.landmark || (this.selectedBooking && this.selectedBooking.landmark) || '',
        prodList: prodList,
        // The return collected on the same visit (fetchReturnFor, SAP first).
        prodListRt: ret ? ret.rows : [],
        soNo: this.soNumber,
        rtNo: ret ? String(ret.rt.rtNo) : '',
        rtDocEntry: ret ? ret.rt.docEntry : null,
        city: soData.city || '',
        payMent: payMent,
        time: time
      };
      const sheet = this._sheet();
      Object.assign(job, this._plan());
      const stampAt = new Date().toISOString().slice(0,10) + " " + new Date().toTimeString().slice(0,8);
      const deliveryDoc = DR.listRow(job, sheet, stampAt);
      const timelineDoc = DR.timelineDoc(job, sheet, stampAt);

      // Create batch operation
      const batch = db.batch();

      // Add documents to batch
      const deliveryRef = db.collection("deliveryList").doc();
      const timelineRef = db.collection("deliveryTimeline").doc();

      batch.set(deliveryRef, deliveryDoc);
      batch.set(timelineRef, timelineDoc);

      // Commit batch
      await batch.commit();

      return {
        deliveryId: deliveryRef.id,
        deliveryDoc: deliveryDoc,
        timelineId: timelineRef.id,
        timelineDoc: timelineDoc
      };
    } catch (error) {
      throw { 
          type: 'firestore', 
          message: ['ไม่สามารถบันทึกข้อมูลการจัดส่งได้',
            'Add to firestore collection error : deliveryList , deliveryTimeline',
            error
          ]
        };
    }
    
  },
  async updateDeliveryToFirestore(){
    try {
      let time = this._prepareTime();

      // Get references to the documents to be updated
      // const deliveryRef = db.collection("deliveryList").doc(this.selectedBooking.id);
      // A return-only row is found by its RT: it has no SO, and its timeline
      // document's trackNo is the RT number.
      const byField = this.returnOnly ? 'rtNo' : 'soNo';
      const trackNo = this.returnOnly ? this.rtNumber : this.soNumber;
      let deliveryRef;
      // The row's return, for the caller to move in SAP beside it.
      let rtDocEntry = (this.selectedBooking && this.selectedBooking.rtDocEntry) || null;
      if (this.transportFormMode === 'move') {
          const deliveryQuery = await db.collection("deliveryList")
              .where(byField, "==", trackNo)
              .limit(1)
              .get();
          
          if (!deliveryQuery.empty) {
              // Use the first document's ID
              deliveryRef = db.collection("deliveryList").doc(deliveryQuery.docs[0].id);
              rtDocEntry = deliveryQuery.docs[0].data().rtDocEntry || null;
          } else {
              // Handle case where no matching document is found
              throw new Error("No delivery found with the given SO number");
          }
      } else {
          // Use the selectedBooking.id as before
          deliveryRef = db.collection("deliveryList").doc(this.selectedBooking.id);
      }

      const timelineQuery = await db.collection("deliveryTimeline")
        .where("trackNo", "==", trackNo)
        .get();
      if (timelineQuery.empty) throw {
        type: 'so',
        message: ['ไม่พบข้อมูลการจัดส่งของ SO นี้ในระบบ',
        'ไม่พบข้อมูลใน deliveryTimeline',
        'โปรดแจ้งผู้ดูแลระบบ']
      };
      const timelineRef = timelineQuery.docs[0].ref;

      // Create batch operation
      const batch = db.batch();
      const now = new Date()
      let curDateTime = now.toISOString().slice(0,10) + " " + now.toTimeString().slice(0,8);

      batch.update(deliveryRef, {
        transNo: this.selectedSlotNewTransNo,
        time: time,
        loadDate: this.transportForm.loadDate,
        regNum: this.transportForm.carNumber,
        driver: this.selectedDriverCodeAndName,
        driverName: this.selectedDriverName || '',
        deliveryDate: this.transportForm.deliveryDate,
        timeUpdate: time,
        updateDate: curDateTime,
        updateBy: this.user
      });

      // Update deliveryTimeline document
      batch.update(timelineRef, {
        date: this.transportForm.deliveryDate,
        timeLine: [{ time: time, desc: "กำลังส่งสินค้า", icon: "mdi-truck-fast-outline"}],
        driverName: this.selectedDriverName|| '',
        regNum: this.transportForm.carNumber,
      });
      
      // Commit batch
      await batch.commit();
      
      return {
        deliveryId: deliveryRef.id,
        timelineId: timelineRef.id,
        rtDocEntry: rtDocEntry,
      };
    } catch (error) {
      throw { 
        type: 'firestore', 
        message: ['ไม่สามารถบันทึกข้อมูลการจัดส่งได้',
          'Update firestore collection error : deliveryList , deliveryTimeline',
          error
        ]
      };
    }
    
  },
  // Returns the maximum allowed load date (1 day before delivery)
  calculateMaxLoadDate() {
    if (!this.transportForm.deliveryDate) return null;
    const maxDate = new Date(this.transportForm.deliveryDate);
    maxDate.setDate(maxDate.getDate() - 1); // Subtract 1 day
    return maxDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  },
  /**
   * Find a specific time slot record with optimized date lookup
   * @param {string} carName - Name of the car to search for
   * @param {string} targetDate - Date in 'YYYY-MM-DD' format
   * @param {string} timeSlot - 'AM', 'PM', or 'OM'
   * @param {string} slotTimeId - ID of the specific time slot record
   * @returns {object|null} Found record or null if not found
   */
  findTimeSlotRecord(carName, targetDate, timeSlot, slotTimeId) {
    // 1. Find the car by name
    const car = this.cars.find(c => c.name === carName);
    if (!car || !car.slots || car.slots.length === 0) {
      reportWarning(`Car with name ${carName} not found or has no slots`);
      return null;
    }

    // 2. Calculate slot index directly from date difference
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to midnight
    
    const target = new Date(targetDate);
    const diffDays = Math.floor((target - today) / (1000 * 60 * 60 * 24));
    
    // Validate date range (0-29 since first slot is today)
    if (diffDays < 0 || diffDays >= car.slots.length) {
      reportWarning(`Date ${targetDate} is out of range (must be within ${car.slots.length} days from today)`);
      return null;
    }

    // Directly access the slot by calculated index
    const slot = car.slots[diffDays];
    
    // Verify the date matches as a safety check
    if (slot.date !== targetDate) {
      reportError(`Date mismatch at index ${diffDays}: expected ${targetDate}, found ${slot.date}`);
      return null;
    }

    // 3. Get the time slot array
    const timeSlotArray = slot[timeSlot];
    if (!timeSlotArray || !Array.isArray(timeSlotArray)) {
      reportWarning(`No ${timeSlot} records found for ${targetDate}`);
      return null;
    }

    // 4. Find the specific record by ID
    const record = timeSlotArray.find(item => item.id === slotTimeId);
    if (!record) {
      reportWarning(`Record with ID ${slotTimeId} not found in ${timeSlot} slot`);
      return null;
    }

    // return {
    //   // car,
    //   // slot,
    //   // timeSlot,
    //   record
    // };
    return record
  },

  // Group of methods for created()
  async getSmallCarData() {
    try {
        // Reads the per-plate documents in registerCar, not the legacy
        // carSmall array -- see src/components/fleet.js. loadFleet caches into
        // sessionStorage.carSmall itself, so the ordering here is unchanged.
        const { small } = await loadFleet();

        return small;
    } catch (error) {
        throw error;
    }
  },

  initializeCarsStructure() {
    this.cars = this.carRegNumSmall.map((name, index) => ({
      id: index + 1,
      name: `${name}`,
      slots: Array(30).fill().map((_, slotIndex) => {
        const slotDate = new Date();
        slotDate.setDate(slotDate.getDate() + slotIndex);

        const deliveryDateStr = slotDate.toISOString().split('T')[0];
        
        // by default loaded date alway one day before delivery's date
        slotDate.setDate(slotDate.getDate() - 1 );
        // Check if the resulting date is Sunday (0 = Sunday)
        if (slotDate.getDay() === 0) {
            slotDate.setDate(slotDate.getDate() - 1); // Subtract one more day to get Saturday
        }
        // Set loaddate
        const loadDateStr = slotDate.toISOString().split('T')[0];

        return {
          // date: slotDate.toISOString().split('T')[0],
          date: deliveryDateStr,
          zoneMain: null,
          zoneDv: [],
          district:null,
          AM: [],
          PM: [],
          OM: [],
          totalVolume: 0,
          driverName: '',
          transNo: '',
          deliveryDate:deliveryDateStr,
          loadDate: loadDateStr
        };
      })
    }));
  },
  
  async getSoBookingFor30Days() {
    const today = new Date();
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(today.getDate() + 30);

    // const regNumArray = ["1ฒบ-7127", "ตค-7203", "1ฒห-156", "2ฒฆ-2748", "3ฒฌ-5175"]; 
    const regNumArray = sessionStorage.carSmall.split(",");

    const documentsArray = [];

    try {
      const deliveryListRef = db.collection('deliveryList');
      const snapshot = await deliveryListRef
        .where('deliveryDate', '>=', today.toISOString().split('T')[0])
        .where('deliveryDate', '<=', thirtyDaysLater.toISOString().split('T')[0])
        .where('regNum', 'in', regNumArray)
        .get();

      if (snapshot.empty) {
        return documentsArray;
      }

      snapshot.forEach(doc => {
        documentsArray.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return documentsArray;
    } catch (error) {
      reportError('Error retrieving documents: ', error);
      return documentsArray;
    }
  },
  
  async getBookingFor30Days() {
    const today = new Date();
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(today.getDate() + 30);

    const documentsArray = [];

    try {
      const bookingRef = db.collection('booking');
      const snapshot = await bookingRef
        .where('date', '>=', today.toISOString().split('T')[0])
        .where('date', '<=', thirtyDaysLater.toISOString().split('T')[0])
        .get();

      if (snapshot.empty) {
        return documentsArray;
      }

      snapshot.forEach(doc => {
        documentsArray.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return documentsArray;
    } catch (error) {
      reportError('Error retrieving booking documents: ', error);
      return documentsArray;
    }
  },
  
  addZoneInfo(booking){
    const custAddress = booking.custAddress || '';
    let zip = '';
    let subDt = '';
    let district = '';

    const zipMatch = custAddress.match(/\b\d{5}\b/);
    if (zipMatch) {
        zip = zipMatch[0];
    }

    // const subDtMatch = custAddress.match(/(?:^|\s)(?:ตำบล|แขวง|ต\.|ตำบล\/แขวง|แขวง\/ตำบล|ตำบลแขวง|แขวงตำบล)\.?\s*([^ ,/]+)/);
    const subDtMatch = custAddress.match(/(?:^|\s)(?:ตำบล|แขวง|เเขวง|ต\.|ตำบล\/แขวง|แขวง\/ตำบล|ตำบลแขวง|แขวงตำบล)\.?\s*([^ ,/]+)/);
    const districtMatch = custAddress.match(/(?:เขต|อำเภอ|อ\.(?:\s*(?:เขต|อำเภอ))?|เขต\/อำเภอ|อำเภอ\/เขต|เขตอำเภอ|อำเภอเขต)\s*([^ ,/]+)/);

    if (subDtMatch) {
        subDt = subDtMatch[1];
    }
    if (districtMatch) {
        district = districtMatch[1];
    }

    booking.custAzip=zip
    booking.custAsubDt=subDt
    booking.custAdistrict=district

    booking.zoneDv = [];
    booking.zoneMain = null;
    booking.zoneMax = null;
    booking.custZsubdt = ""
    booking.custZdistrict = ""
    booking.custZzip = ""

    if (custAddress) {
        const zoneInfo = zoneDv.find(zone => {
            return (
                String(zone.zip) === String(zip) &&
                zone.subDt === subDt
                // zone.subDt === subDt &&
                // zone.district === district
            );
        });
        booking.zoneInfo = zoneInfo

        if (zoneInfo) {
            booking.zoneDv = zoneInfo.zoneDv;
            booking.zoneMax = zoneInfo.zoneMax;
            booking.zoneMain = zoneInfo.zoneMain;
            booking.custZsubdt = zoneInfo.subDt
            booking.custZdistrict = zoneInfo.district
            booking.custZzip = zoneInfo.zip
        }
      }

      // return booking;
  },

  updateZoneInfo(slot, newZoneMain, newZoneDv) {
    if (slot.zoneMain === null || slot.zoneMain === undefined || 
        slot.zoneDv === null || slot.zoneDv === undefined || !slot.zoneDv.length) {
        slot.zoneMain = [newZoneMain];
        slot.zoneDv = [...newZoneDv];
    } else {
        if (slot.zoneDv.includes(newZoneMain)) {
            if (!slot.zoneMain.includes(newZoneMain)) {
                slot.zoneMain.push(newZoneMain);
            }

            newZoneDv.forEach(zone => {
                if (!slot.zoneDv.includes(zone)) {
                    slot.zoneDv.push(zone);
                }
            });
        } else {
            reportWarning('Selected zone is not allowed for this slot');
            return false;
        }
    }
    return true;
  },
  
  addSoBookingToCars(soBookingLst, cars) {
    soBookingLst.forEach(booking => {
      // const { regNum, deliveryDate, time, zoneMain, zoneDv } = booking;
      const { regNum, deliveryDate, loadDate, driverName, time, zoneMain, zoneDv, prodList , transNo } = booking;

      const car = cars.find(car => car.name.includes(regNum));
      if (!car) {
          reportWarning(`No matching car found for regNum: ${regNum}`);
          return;
      }

      const slotType = getTimeSlot(time);
      const slotIndex = getSlotIndex(deliveryDate);
      const slot = car.slots[slotIndex];

      // Add deliveryDate, loadDate, and driverName to the slot
      slot.deliveryDate = deliveryDate;
      slot.loadDate = loadDate;
      slot.driverName = driverName;
      slot.transNo = transNo;

      // Initialize totalVolume if not set, then increment
      slot.totalVolume = (slot.totalVolume || 0) + 
                         prodList.reduce((sum, product) => sum + (product.volume || 0), 0);

      this.updateZoneInfo(slot, zoneMain, zoneDv);
      slot[slotType].push(booking);
    });
  },
  
  addBookingToCars(bookingList, cars) {
    bookingList.forEach(booking => {
      const { carName, date, slot, zoneMain, zoneDv } = booking;

      const car = cars.find(car => car.name.includes(carName));
      if (!car) {
        reportWarning(`No matching car found for name: ${carName}`);
        return;
      }

      const slotIndex = getSlotIndex(date);
      const daySlot = car.slots[slotIndex];

      if (zoneMain || zoneDv) {
        this.updateZoneInfo(daySlot, zoneMain, zoneDv);
      }

      daySlot[slot].push(booking);
    });
  },

  // Set up Firestore real-time listeners
  setupRealTimeListeners() {
    // SO Booking listener
    const regNumArray = sessionStorage.carSmall.split(",");
    this.soBookingUnsubscribe = db.collection('deliveryList')
      // .where('regNum', 'in', ["1ฒบ-7127", "ตค-7203", "1ฒห-156", "2ฒฆ-2748", "3ฒฌ-5175"])
      .where('regNum', 'in', regNumArray)
      .where('deliveryDate', '>=', this.getFormattedDate(new Date()))
      .where('deliveryDate', '<=', this.getFormattedDate(this.getDateDaysLater(30)))
      .onSnapshot(snapshot => {
        const changes = snapshot.docChanges();
        if (changes.length === 0) return;
        
        const updatedBookings = changes.map(change => ({
          id: change.doc.id,
          ...change.doc.data()
        }));
        
        this.processSoBookingChanges(updatedBookings, changes);

        // Process zones before adding to cars
        // const bookingsWithZones = this.addZoneInfo(updatedBookings, this.zoneDv);
        // this.processSoBookingChanges(bookingsWithZones, changes);
      });
    
    // Regular Booking listener
    this.bookingUnsubscribe = db.collection('booking')
      .where('date', '>=', this.getFormattedDate(new Date()))
      .where('date', '<=', this.getFormattedDate(this.getDateDaysLater(30)))
      .onSnapshot(snapshot => {
        const changes = snapshot.docChanges();
        if (changes.length === 0) return;
        
        const updatedBookings = changes.map(change => ({
          id: change.doc.id,
          ...change.doc.data()
        }));
        
        this.processBookingChanges(updatedBookings, changes);
      });
    
    // Locked slots listener
    this.lockedUnsubscribe = db.collection('locked')
      .where('regNum', 'in', regNumArray)
      .where('date', '>=', this.getFormattedDate(new Date()))
      .where('date', '<=', this.getFormattedDate(this.getDateDaysLater(30)))
      .onSnapshot(snapshot => {
        const changes = snapshot.docChanges();
        if (changes.length === 0) return;
        
        changes.forEach(change => {
          const lockedData = change.doc.data();
          const car = this.cars.find(c => c.name.includes(lockedData.regNum));
          if (!car) return;
          
          const slotIndex = this.getSlotIndex(lockedData.date);
          const slot = car.slots[slotIndex];
          
          // Update locked status for AM/PM slots
          if (lockedData.am !== undefined) {
            // slot.lockedAm = lockedData.am;
            this.$set(slot,'lockedAm',lockedData.am)
          }
          if (lockedData.pm !== undefined) {
            // slot.lockedPm = lockedData.pm;
            this.$set(slot,'lockedPm',lockedData.pm)
          }
          
          // Trigger UI update if needed
          // this.$forceUpdate();
        });
      });
  },

  // Process changes from deliveryList
  processSoBookingChanges(bookings, changes) {
    // Create Sets for removed and modified booking IDs
    const removedIds = new Set();
    const modifiedIds = new Set();

    // First process all removed and modified bookings
    changes.forEach(change => {
      if (change.type === 'removed' || change.type === 'modified') {
        const bookingData = change.doc.data();
        const booking = { id: change.doc.id, ...bookingData };

        if (change.type === 'removed') {
          removedIds.add(change.doc.id);
          this.removeBookingFromCars(booking);
        } else { // modified
          modifiedIds.add(change.doc.id);
          this.removeBookingFromCars(booking);
        }
      }
    });

    // Then process all bookings (add or re-add modified ones)
    bookings.forEach(booking => {
      // Skip if this booking was removed
      if (removedIds.has(booking.id)) {
        return;
      }

      const { regNum, deliveryDate, time } = booking;
      const car = this.cars.find(c => c.name.includes(regNum));
      if (!car) return;

      // update driver to car
      const driverCode = booking.driver?booking.driver.split('-')[0].trim():'';
      car.driverName = booking.driverName;
      car.driverCode = driverCode;

      const slotIndex = this.getSlotIndex(deliveryDate);
      const slotType = this.getTimeSlot(time);
      const slot = car.slots[slotIndex];

      // Update slot metadata
      slot.deliveryDate = booking.deliveryDate;
      slot.loadDate = booking.loadDate;
      slot.driverName = booking.driverName;
      slot.driverCode = driverCode;
      slot.transNo = booking.transNo;
      
      // Update volume
      // slot.totalVolume = slot.totalVolume + booking.prodList.reduce((sum, product) => 
      //   sum + (product.volume || 0), 0);
      slot.totalVolume = parseFloat((
        slot.totalVolume + booking.prodList.reduce((sum, product) => 
          sum + (product.volume || 0), 0)
      ).toFixed(2));

      // Add Zone info
      this.addZoneInfo(booking)
      
      // Update zones
      this.updateZoneInfo(slot, booking.zoneMain, booking.zoneDv);

      // Add first sub District destination to slot
      if(!slot.subDt && booking.custAdistrict){
        slot.district = booking.custAdistrict;
      }
      
      // Only add the booking (modified ones were already removed in first loop)
      slot[slotType].push(booking);

    });
  },

  
  // Process changes from booking collection
  processBookingChanges(bookings, changes) {
    // Create Sets for removed and modified booking IDs for quick lookup
    const removedIds = new Set();
    const modifiedIds = new Set();

    // First process all removed and modified bookings
    changes.forEach(change => {
      if (change.type === 'removed' || change.type === 'modified') {
        const bookingData = change.doc.data();
        const booking = { id: change.doc.id, ...bookingData };

        if (change.type === 'removed') {
          removedIds.add(change.doc.id);
          this.removeBookingFromCars(booking);
        } 
        else { // modified
          modifiedIds.add(change.doc.id);
          this.removeBookingFromCars(booking);
        }
      }
    });
    
    // Then process all bookings (add or re-add modified ones)
    bookings.forEach(booking => {
      // Skip if this booking was removed
      if (removedIds.has(booking.id)) {
        return;
      }

      const { carName, date, slot } = booking;
      const car = this.cars.find(c => c.name.includes(carName));
      if (!car) return;

      const slotIndex = this.getSlotIndex(date);
      const daySlot = car.slots[slotIndex];
      
      // Update zones if provided
      if (booking.zoneMain || booking.zoneDv) {
        this.updateZoneInfo(daySlot, booking.zoneMain, booking.zoneDv);
      }
      
      // Only add the booking (modified ones were already removed in first loop)
      daySlot[slot].push(booking);
      
    });
  },

  // The volume a deliveryList row added to its slot, taken back when the row
  // leaves it. processSoBookingChanges handles a MODIFIED row by removing it and
  // adding it again, and the add increments totalVolume -- so without this every
  // edit of a row (a time change, a พิกัด typed in the slot dialog) counted its
  // volume once more, and so did a deleted row that was never subtracted. The
  // removed object is the one the slot held, so its prodList is the one that was
  // added, even when the edit changed it.
  releaseSoVolume(slot, removed) {
    if (!removed || !RJ.isListRow(removed) || !Array.isArray(removed.prodList)) return;
    const vol = removed.prodList.reduce((sum, product) => sum + (product.volume || 0), 0);
    slot.totalVolume = parseFloat(Math.max(0, (slot.totalVolume || 0) - vol).toFixed(2));
  },

  removeBookingFromCars(booking) {
    // 0. check SO that car's registration number is not in small cars list
    if(RJ.isListRow(booking) && booking.regNum && !this.carRegNumSmall.includes(booking.regNum)){
      return;
    }
    
    // 1. First try to find the booking using the most direct approach
    let found = false;
    
    // Try to find by current car info first (for modified bookings)
    if (booking.regNum || booking.carName) {
        const carIndex = this.cars.findIndex(c => 
            RJ.isListRow(booking)
                ? c.name.includes(booking.regNum) 
                : c.name.includes(booking.carName)
        );
        
        if (carIndex !== -1) {
            const date = RJ.isListRow(booking) ? booking.deliveryDate : booking.date;
            const slotIndex = this.getSlotIndex(date);
            const slotType = this.getTimeSlot(booking.time || booking.slot);
            
            if (slotIndex !== -1 && slotType) {
                const car = this.cars[carIndex];
                const slot = car.slots[slotIndex];
                
                if (slot && slot[slotType]) {
                    const bookingIndex = slot[slotType].findIndex(b => b.id === booking.id);
                    if (bookingIndex !== -1) {
                        this.releaseSoVolume(slot, slot[slotType].splice(bookingIndex, 1)[0]);
                        found = true;
                    }
                }
            }
        }
    }
    
    // 2. If not found, search through all cars (fallback for modified bookings)
    if (!found) {
        for (const car of this.cars) {
            for (const slot of car.slots) {
                for (const slotType of ['AM', 'PM', 'OM']) {
                    if (slot[slotType]) {
                        const bookingIndex = slot[slotType].findIndex(b => b.id === booking.id);
                        if (bookingIndex !== -1) {
                            this.releaseSoVolume(slot, slot[slotType].splice(bookingIndex, 1)[0]);
                            found = true;
                            break;
                        }
                    }
                }
                if (found) break;
            }
            if (found) break;
        }
    }
    
    if (!found) {
        reportWarning(`Booking ${booking.id} not found in any car`);
    }
  },

  // Helper methods
  getFormattedDate(date) {
    return date.toISOString().split('T')[0];
  },

  getDateDaysLater(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  },

  getTimeSlot(time) {
    if (!time) return 'OM';
    if (time === 'AM' || time === 'PM') return time;
    
    const [startTime] = time.split(' - ');
    const startHour = parseInt(startTime.split(':')[0], 10);
    
    if (startHour >= 4 && startHour < 12) return 'AM';
    if (startHour >= 12 && startHour <= 23) return 'PM';
    return 'OM';
  },

  getSlotIndex(date) {
    const bookingDate = new Date(date);
    if (isNaN(bookingDate.getTime())) {
      reportError(`Invalid date: ${date}`);
      return 0;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffInDays = Math.floor((bookingDate - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(diffInDays, 29));
  },
  
  async getActiveDrivers() {
    try {
      const snapshot = await db.collection('driver')
        .where('active', '==', true)
        .get();

      // this.driverLst = []; // Clear existing list

      if (snapshot.empty) {
        return;
      }

      snapshot.forEach(doc => {
        const data = doc.data();
        this.driverLst.push({
          driverCode: data.driverCode,
          driverName: data.driverName
        });
      });

      // sorting by driverName
      this.driverLst.sort((a, b) => a.driverName.localeCompare(b.driverName));

    } catch (error) {
      reportError('Error retrieving active drivers: ', error);
      this.driverLst = [];
    }
  }
},

async created(){
  // Initialize cars structure
  this.carRegNumSmall = await this.getSmallCarData()
  this.initializeCarsStructure();
  
  // Set up real-time listeners
  this.setupRealTimeListeners();

  // Which Sundays of the window delivery opened, so they are not drawn as days off.
  const from = this.localYmd(this.days[0].date);
  const to = this.localYmd(this.days[this.days.length - 1].date);
  this.bookingDayUnsubscribe = S.subscribeMonth(from, to, byDate => {
    const open = {};
    Object.keys(byDate).forEach(d => { if (R.isSundayOpen(byDate[d])) open[d] = true; });
    this.openSundays = open;
  }, e => reportWarning('bookingDay (open Sundays) listener failed', e));

  // A car on day 2 of a route sent to an out-of-area province is not free:
  // marked on its column so nobody books it by hand without noticing.
  try { this.routeHolds = await DSV.loadRouteHolds(from, to); } catch (e) { reportWarning('route holds failed', e); }

  //get active driver
  this.getActiveDrivers();

},

beforeDestroy() {
    // Clean up listeners
    if (this.soBookingUnsubscribe) this.soBookingUnsubscribe();
    if (this.bookingUnsubscribe) this.bookingUnsubscribe();
    if (this.lockedUnsubscribe) this.lockedUnsubscribe();
    if (this.bookingDayUnsubscribe) this.bookingDayUnsubscribe();
}
};
</script>

<style scoped>
.sticky-search {
  position: sticky;
  /* top: 0; */
  top: 64px;
  z-index: 100;
  background-color: white; /* or your preferred background */
  padding-top: 16px;
  padding-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1); /* optional shadow for better visibility */
}

.sticky-car-header {
  position: sticky;
  top: 200px; /* Height of your search bar + padding */
  z-index: 101; /* Lower than search bar's z-index */
  background-color: white;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0; /* Visual separation */
  margin-top: -1px; /* Prevents double border if search has shadow */
}
.car-container {
  position: relative;
  margin-bottom: 16px; /* Space between cars */
}

.sticky-car-info {
  position: sticky;
  /* top: 196px;  */
  top: 196px; /* Below search bar */
  z-index: 99;
  background-color: white;
  padding: 0px 0;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
@media (max-width: 600px) {
  .sticky-search {
    top: 56px; 
  }
  /* .sticky-car-header {
    top: 500px;
  } */
  .sticky-car-info {
    top: 256px; /* Below search bar on mobile */
  }
}

.car-content {
  position: relative;
  z-index: 90;
}
.time-slot {
min-width: 80px;
width: 100%;
display: flex;
justify-content: center;
align-items: center;
border-radius: 5px;
height: 32px;
cursor: pointer;
}
.car-name {
font-weight: bold;
padding-left: 8px;
}
.route-hold{
  cursor: pointer;
  text-align: center;
  font-size: 11px;
}
.route-hold:hover{
  text-decoration: underline;
}
.plan-day-head td{
  background: #e8eaf6;
  font-weight: 600;
  color: #283593;
}
.slot-heading{
  cursor: pointer;
}
.slot-heading:hover{
  background-color:#FF9800;
  color: white;
}
.date-slot {
display: flex;
flex-direction: column;
flex: 0 0 80px;
margin: 0px;
align-items: stretch;
box-sizing: border-box;
padding: 4px;
}
.sunday {
background-color: rgba(255, 0, 0, 0.1);
border-radius: 4px;
}
.sunday-slot {
background-color: #ff5252 !important;
border-color: #ff5252 !important;
}
.car-row {
border-bottom: 1px solid #e0e0e0;
margin-bottom: 10px;
/* padding: 12px 0; */
box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
}
.car-row:last-child {
border-bottom: none;
}
.even-row {
background-color: #f8e9e9;
}
.odd-row {
background-color: #e9f8f8;
}
.product-row {
margin-bottom: 12px;
display: flex;
align-items: center;
gap: 32px;
}

.product-checkbox {
flex: 1;
min-width: 120px;
}

.product-checkbox >>> .v-input {
margin-top: 0;
margin-bottom: 0;
}

.product-checkbox >>> .v-label {
font-size: 14px;
}
.stepper-input {
display: flex;
align-items: stretch;
height: 36px;
border-radius: 4px;
border: 1px solid #e0e0e0;
background: #fff;
width: 120px;
}

.stepper-button {
width: 36px;
min-width: 36px;
height: 100%;
display: flex;
align-items: center;
justify-content: center;
background: none;
border: none;
cursor: pointer;
touch-action: manipulation;
transition: background-color 0.2s;
}

.stepper-button:hover {
background-color: #f5f5f5;
}

.stepper-button:active {
background-color: #eeeeee;
}

.quantity-display {
flex: 1;
display: flex;
align-items: center;
justify-content: center;
font-size: 14px;
border-left: 1px solid #e0e0e0;
border-right: 1px solid #e0e0e0;
min-width: 36px;
}

.flex-wrap {
display: flex;
flex-wrap: wrap;
gap: 8px;
align-items: flex-start;
}

.separator {
display: inline-block;
width: 8px;
text-align: center;
font-weight:900;
margin-left: 4px;
}
.invisible {
visibility: hidden;
}

@media (max-width: 960px) {
.time-slot {
  min-width: 70px;
  width: 100%;
}
.date-slot {
  width: 100%;
}
.product-row {
  gap: 24px;
  margin-bottom: 16px;
  padding: 0 8px;
}

.product-checkbox {
  flex: 1;
}

.stepper-wrapper {
  min-width: 110px;
}

.stepper-input {
  width: 110px;
  height: 36px;
}

.stepper-button {
  width: 36px;
  min-width: 36px;
}

.quantity-display {
  min-width: 36px;
}

.product-checkbox >>> .v-label {
  font-size: 14px;
  line-height: 1.2;
}
}

.stepper-button {
-webkit-tap-highlight-color: transparent;
user-select: none;
-webkit-touch-callout: none;
}

/* Slot details dialog styles */
.slot-details-table {
width: 100%;
border-collapse: collapse;
}

.slot-details-table th, 
.slot-details-table td {
padding: 8px 12px;
border-bottom: 1px solid #e0e0e0;
text-align: left;
}

.slot-details-table th {
background-color: #f5f5f5;
font-weight: 500;
}

.slot-details-table tr:hover {
background-color: #f9f9f9;
}

.empty-slot-message {
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
padding: 24px;
color: #757575;
}

.time-cell {
  min-height: 24px;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  min-width: 100px;  /* Ensures '-' has enough click area */
  text-align: center;
}

.so-number-cell {
  min-height: 24px;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  min-width: 80px;  /* Ensures '-' has enough click area */
  text-align: center;
}

.editable {
  color: #1976D2;
  cursor: pointer;
  transition: all 0.2s ease;
}
.editable:hover {
  background-color: #1976D2;
  color: white;
}

.non-editable {
  cursor: default;
  color: inherit;
}
</style>

<style>
/* Unscoped: the wrapper is rendered inside v-simple-table, out of reach of
   the scoped block. Leaves room in the 90%-high dialog for the title and the
   เพิ่ม SO row, so the table scrolls rather than the whole card. */
.slot-details-table .v-data-table__wrapper {
  max-height: 65vh;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
</style>